import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Camera, Edit, Share, Users, UserPlus, UserCheck, Heart, Video, Trophy, Crown, Star, 
  MapPin, Calendar, Save, MessageCircle, UserMinus,
  Play, Image as ImageIcon, Grid, List, MoreHorizontal, Send,
  Copy, ExternalLink, HelpCircle, Trash2, Music, Pause, ChevronLeft, ChevronRight,
  Search, X, Plus, Volume2, VolumeX
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { openMediaLightbox } from '../utils/mediaLightbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

import { API_BASE_URL } from '../config';
import { CurrentEventsPostBody } from './CurrentEventsCards';

const API_URL = API_BASE_URL;

// Custom Icons
const EmeraldIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <defs>
      <linearGradient id="emeraldShineProfile" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="50%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 8L12 22L20 8L12 2Z" fill="url(#emeraldShineProfile)" stroke="#047857" strokeWidth="1"/>
    <path d="M12 2L4 8L12 12L20 8L12 2Z" fill="#6ee7b7" fillOpacity="0.4"/>
  </svg>
);

const RubyIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <defs>
      <linearGradient id="rubyShineProfile" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f87171" />
        <stop offset="50%" stopColor="#ef4444" />
        <stop offset="100%" stopColor="#dc2626" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 8L12 22L20 8L12 2Z" fill="url(#rubyShineProfile)" stroke="#b91c1c" strokeWidth="1"/>
    <path d="M12 2L4 8L12 12L20 8L12 2Z" fill="#fca5a5" fillOpacity="0.4"/>
  </svg>
);

const RoseQuartzIcon = ({ className = "h-5 w-5", style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="none">
    <defs>
      <linearGradient id="roseQuartzShineProfile" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f9a8d4" />
        <stop offset="50%" stopColor="#ec6fa6" />
        <stop offset="100%" stopColor="#d6467f" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 8L12 22L20 8L12 2Z" fill="url(#roseQuartzShineProfile)" stroke="#b03a6b" strokeWidth="1"/>
    <path d="M12 2L4 8L12 12L20 8L12 2Z" fill="#fbcfe2" fillOpacity="0.4"/>
  </svg>
);

// Sapphire Badge Icon - Celebrity Verified (Blue gem stone)
const SapphireBadgeIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sapphireGradProfile" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#60A5FA" />
        <stop offset="50%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#1D4ED8" />
      </linearGradient>
    </defs>
    <path d="M12 2L5 7L12 22L19 7L12 2Z" fill="url(#sapphireGradProfile)" stroke="#1E40AF" strokeWidth="0.5"/>
    <path d="M12 2L5 7H19L12 2Z" fill="#93C5FD" fillOpacity="0.7"/>
    <path d="M5 7L12 11L12 22L5 7Z" fill="#3B82F6"/>
    <path d="M19 7L12 11L12 22L19 7Z" fill="#1D4ED8"/>
    <path d="M9 5L11 7L9 9" fill="#BFDBFE" fillOpacity="0.8"/>
  </svg>
);

// Canary Badge Icon - Official GEMS Live Verified (Yellow/Gold gem stone)
const CanaryBadgeIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="canaryGradProfile" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FDE047" />
        <stop offset="50%" stopColor="#FACC15" />
        <stop offset="100%" stopColor="#EAB308" />
      </linearGradient>
    </defs>
    <path d="M12 2L5 7L12 22L19 7L12 2Z" fill="url(#canaryGradProfile)" stroke="#A16207" strokeWidth="0.5"/>
    <path d="M12 2L5 7H19L12 2Z" fill="#FEF08A" fillOpacity="0.7"/>
    <path d="M5 7L12 11L12 22L5 7Z" fill="#FACC15"/>
    <path d="M19 7L12 11L12 22L19 7Z" fill="#EAB308"/>
    <path d="M9 5L11 7L9 9" fill="#FEF9C3" fillOpacity="0.8"/>
    <path d="M8 12L11 15L16 9" stroke="#713F12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Referral Code reveal — collapses the code behind a "Show" toggle so
// it isn't visible to a passer-by / screen recorder by default. Copy
// and Share actions use the Web Share API when available, then fall
// back to clipboard.
const ReferralCodeReveal = ({ code }) => {
  const [shown, setShown] = React.useState(false);
  const shareText = `Join me on GEMS — use code ${code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Code copied');
    } catch (_e) {
      toast.error('Copy failed');
    }
  };
  const share = async () => {
    const payload = { title: 'GEMS Live', text: shareText };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success('Share text copied');
      }
    } catch (_e) { /* user cancelled */ }
  };
  return (
    <div className="space-y-2" data-testid="referral-code-reveal">
      <div className="flex items-center gap-2">
        <div
          className={`flex-1 font-mono tracking-widest text-lg py-2 px-3 rounded-lg border ${shown ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300' : 'bg-slate-900/60 border-slate-700 text-slate-500 select-none'}`}
          data-testid="referral-code-value"
        >
          {shown ? code : '••••••••••'}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShown((v) => !v)}
          data-testid="referral-code-toggle"
        >
          {shown ? 'Hide' : 'Show'}
        </Button>
      </div>
      {shown && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={copy}
            data-testid="referral-code-copy"
          >
            <Copy className="w-3 h-3 mr-1" /> Copy code
          </Button>
          <Button
            type="button"
            size="sm"
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={share}
            data-testid="referral-code-share"
          >
            <Share className="w-3 h-3 mr-1" /> Share
          </Button>
        </div>
      )}
    </div>
  );
};

const ProfilePage = ({ user, NavBar, AuthContext, viewHandle }) => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const loggedInUser = user || authContext?.user;
  // Visitor mode: when a handle is in the URL and it isn't the logged-in
  // user's own, this same component renders READ-ONLY from the target
  // user's public profile — so what a user builds/edits on their page is
  // exactly what everyone else sees when they open it.
  const [viewedUser, setViewedUser] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const isOwner = !viewHandle || (loggedInUser?.handle && viewHandle === loggedInUser.handle);
  const currentUser = isOwner ? loggedInUser : viewedUser;
  
  // Profile State
  const [profileImage, setProfileImage] = useState(currentUser?.picture || null);
  const [coverImage, setCoverImage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareToFriendsModal, setShowShareToFriendsModal] = useState(false);
  const [friendsToShare, setFriendsToShare] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [selectedFriendsToShare, setSelectedFriendsToShare] = useState([]);
  const [shareMessage, setShareMessage] = useState('Check out my GEMS Live profile!');
  const [showPostModal, setShowPostModal] = useState(false);
  // Which attachment control to show inside the Create Post modal:
  // 'photo' | 'video' | 'any' — set by whichever composer button was
  // tapped so the modal doesn't duplicate the Photo/Video choice.
  const [postAttachmentMode, setPostAttachmentMode] = useState('any');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostMedia, setNewPostMedia] = useState(null);
  const [newPostMediaType, setNewPostMediaType] = useState(null);
  
  // Help & Support state
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpSubject, setHelpSubject] = useState('');
  const [helpCategory, setHelpCategory] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpAttachment, setHelpAttachment] = useState(null);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [helpTab, setHelpTab] = useState('create'); // 'create' | 'tickets'
  
  // Support ticket categories
  const TICKET_CATEGORIES = [
    { id: 'technical', label: 'Technical Issue' },
    { id: 'account', label: 'Account' },
    { id: 'billing', label: 'Billing' },
    { id: 'feedback', label: 'Feedback' },
    { id: 'report', label: 'Report User' },
    { id: 'gameplay', label: 'Gameplay' },
    { id: 'other', label: 'Other' },
  ];
  
  // Track if GEMS tag has been changed (cannot change again)
  const [handleChanged, setHandleChanged] = useState(currentUser?.handle_changed || false);
  
  const [profileData, setProfileData] = useState({
    name: currentUser?.name || 'Player',
    gemsTag: currentUser?.handle || 'player123',
    bio: currentUser?.bio || 'Competitive Spades player and livestreamer 🎴',
    location: currentUser?.location || '',
    gender: currentUser?.gender || '',
    zodiacSign: currentUser?.zodiac_sign || '',
    age: 25,
    // SEP 3 2026: real level (was hardcoded 42 — never matched mobile).
    level: currentUser?.level || Math.max(1, Math.floor((currentUser?.wins || 0) / 10) + 1),
    badges: [], // Removed beta_tester - will add proper badges later
    isStreamer: true,
    isVerified: currentUser?.is_verified || false,
    verifiedBadgeType: currentUser?.verified_badge_type || null
  });
  
  // Location: auto (browser geoloc) or manual (typed + optional suggestion pick).
  // Coords always rounded to 2 decimals (~1.1 km) — privacy rule.
  const [locationAuto, setLocationAuto] = useState((currentUser?.location_mode || 'auto') === 'auto');
  const [locationCoords, setLocationCoords] = useState(currentUser?.location_coords || null);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationNote, setLocationNote] = useState('');
  
  // Stats - Fetch from backend/user data, start at 0 for new users
  const [stats, setStats] = useState({
    fans: currentUser?.fans || 0,
    following: currentUser?.following_list?.length || 0,
    friends: currentUser?.friends_list?.length || 0,
    rubies: currentUser?.rubies || 0,
    emeralds: currentUser?.emeralds || 0,
    gamesWon: currentUser?.wins || 0,
    gamesLost: currentUser?.losses || 0,
    winStreak: currentUser?.streak || 0,
    bestStreak: 15,
    emeraldsSpent: currentUser?.emeralds_spent || 0,
    rubiesEarned: currentUser?.rubies_earned || 0
  });
  
  // Social lists - loaded from API (NO MOCK DATA)
  const [socialListType, setSocialListType] = useState(null); // 'friends' | 'following' | 'fans'
  const [socialListData, setSocialListData] = useState([]);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [loadingSocial, setLoadingSocial] = useState(false);
  
  // Profile Photo Gallery (6 photos)
  const [profilePhotos, setProfilePhotos] = useState(currentUser?.profile_photos || []);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);

  // ══════════════════════════════════════════════════════════════
  // FEB 2026 PROFILE REDESIGN — new state
  // ══════════════════════════════════════════════════════════════
  // 1) Full-page custom background persistence.
  //    background_style ∈ 'cover' | 'contain' | 'repeat' | 'center' | 'blur'
  //    background_overlay is a 0.0–1.0 scrim opacity kept over the image
  //    for text readability.
  const BACKGROUND_STYLE_OPTIONS = [
    { key: 'cover', label: 'Fill' },
    { key: 'contain', label: 'Fit' },
    { key: 'repeat', label: 'Tile' },
    { key: 'center', label: 'Center' },
    { key: 'blur', label: 'Blur' },
  ];
  const [backgroundImage, setBackgroundImage] = useState(currentUser?.background_image || null);
  const [backgroundStyle, setBackgroundStyle] = useState(currentUser?.background_style || 'cover');
  const [backgroundOverlay, setBackgroundOverlay] = useState(
    typeof currentUser?.background_overlay === 'number' ? currentUser.background_overlay : 0.55
  );
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  // Draft state while the picker modal is open — commit on Save,
  // discard on Cancel (Feb 2026 explicit Save/Cancel flow).
  const [draftBgImage, setDraftBgImage] = useState(null);
  const [draftBgStyle, setDraftBgStyle] = useState('cover');
  const [draftBgOverlay, setDraftBgOverlay] = useState(0.55);
  const [savingBackground, setSavingBackground] = useState(false);

  // 2) Photos slideshow — auto-rotate + lightbox.
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [slideshowPaused, setSlideshowPaused] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [photoLikes, setPhotoLikes] = useState({}); // { "0": { likes, liked }, ... }

  // 3) Top-8 Friends grid.
  const [top8Cards, setTop8Cards] = useState([]);
  const [top8Ids, setTop8Ids] = useState([]);
  const [top8IsAuto, setTop8IsAuto] = useState(true);
  const [showTop8Modal, setShowTop8Modal] = useState(false);
  const [top8Search, setTop8Search] = useState('');
  const [allFriendsForTop8, setAllFriendsForTop8] = useState([]);
  const [loadingAllFriends, setLoadingAllFriends] = useState(false);
  const [pickingReplaceSlot, setPickingReplaceSlot] = useState(null); // when full, ask which of 8 to replace
  const [pendingPickFriend, setPendingPickFriend] = useState(null);

  // 4) Song / anthem — real player (Feb 2026).
  //    - `anthem` is the hydrated track object from the backend ({id,title,artist,url,attribution}).
  //    - `audioRef` drives the hidden <audio> element that plays the track on loop.
  //    - `anthemPlaying` / `anthemMuted` mirror the audio element state so the pill UI
  //      updates on every play/pause/mute (equalizer bars animate only when playing).
  //    - The picker modal (`showAnthemPicker`) lists the /api/anthems catalog; each row
  //      has its own preview play button (`previewAnthemId`) that's independent from the
  //      main pill player.
  const [anthem, setAnthem] = useState(currentUser?.anthem || null);
  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);
  const [anthemPlaying, setAnthemPlaying] = useState(false);
  const [anthemMuted, setAnthemMuted] = useState(true); // start muted so autoplay can succeed
  const [showAnthemPicker, setShowAnthemPicker] = useState(false);
  const [anthemCatalog, setAnthemCatalog] = useState([]);
  const [previewAnthemId, setPreviewAnthemId] = useState(null);
  const [savingAnthem, setSavingAnthem] = useState(false);
  // Feb 2026 v2: 5-genre picker filter. 'All' shows everything.
  const [anthemGenres, setAnthemGenres] = useState(['Hip-Hop', 'Pop', 'R&B', 'Classical', 'Country']);
  const [anthemGenreFilter, setAnthemGenreFilter] = useState('All');
  
  // Gender options
  const GENDER_OPTIONS = ['Male', 'Female', 'They/Them', 'Prefer not to say'];
  
  // Zodiac signs
  const ZODIAC_SIGNS = [
    'Aries ♈', 'Taurus ♉', 'Gemini ♊', 'Cancer ♋',
    'Leo ♌', 'Virgo ♍', 'Libra ♎', 'Scorpio ♏',
    'Sagittarius ♐', 'Capricorn ♑', 'Aquarius ♒', 'Pisces ♓'
  ];
  
  // Posts/Content
  const [posts, setPosts] = useState([]);
  
  // Achievements
  const achievements = [
    { id: 1, name: 'First Win', icon: '🏆', color: 'amber', description: 'Won your first game', unlocked: true },
    { id: 2, name: 'Spades Royalty', icon: '👑', color: 'purple', description: '50+ games won', unlocked: true },
    { id: 3, name: 'Streak Master', icon: '🔥', color: 'orange', description: '10 game streak', unlocked: true },
    { id: 4, name: 'Ruby Rich', icon: '❤️', color: 'red', description: 'Earned 5000+ rubies', unlocked: false },
    { id: 5, name: 'Fan Favorite', icon: '⭐', color: 'yellow', description: '1000+ fans', unlocked: true },
    { id: 6, name: 'Live Legend', icon: '📺', color: 'blue', description: '100 hours streamed', unlocked: false },
  ];
  
  // Load real profile from backend (single source of truth — no localStorage
  // cache for shared profile fields; they would go stale vs mobile / other web tabs).
  useEffect(() => {
    const loadSavedData = async () => {
      if (!isOwner) return; // visitors load via the handle effect below (no owner localStorage/auth-me)

      // Fetch real data from backend
      if (currentUser?.id) {
        try {
          // Also fetch user details to get handle_changed status and profile photos
          const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
            withCredentials: true
          });
          if (userResponse.data) {
            setHandleChanged(userResponse.data.handle_changed || false);
            // Update profile data from backend
            setProfileData(prev => ({
              ...prev,
              name: userResponse.data.name || prev.name,
              gemsTag: userResponse.data.handle || prev.gemsTag,
              bio: userResponse.data.bio || prev.bio,
              location: userResponse.data.location || prev.location,
              gender: userResponse.data.gender || prev.gender,
              zodiacSign: userResponse.data.zodiac_sign || prev.zodiacSign,
              verifiedBadgeType: userResponse.data.badge_type || userResponse.data.verified_badge_type || null
            }));
            // Load profile photos
            if (userResponse.data.profile_photos) {
              setProfilePhotos(userResponse.data.profile_photos);
            }
            if (userResponse.data.picture) {
              setProfileImage(userResponse.data.picture);
            }
            if (userResponse.data.cover_image) {
              setCoverImage(userResponse.data.cover_image);
            }
            // Feb 2026 redesign: hydrate background + top8 from user doc
            if (userResponse.data.background_image !== undefined) {
              setBackgroundImage(userResponse.data.background_image || null);
            }
            if (userResponse.data.background_style) {
              setBackgroundStyle(userResponse.data.background_style);
            }
            if (typeof userResponse.data.background_overlay === 'number') {
              setBackgroundOverlay(userResponse.data.background_overlay);
            }
            // Feb 2026: hydrate anthem (hydrated track object) from
            // /auth/me — visitors get it via /users/handle/{handle}.
            if (userResponse.data.anthem !== undefined) {
              setAnthem(userResponse.data.anthem || null);
            }
          }
          
          // Fetch social stats from backend (accurate counts)
          const socialResponse = await axios.get(`${API_URL}/api/users/social/stats`, {
            withCredentials: true
          });
          if (socialResponse.data) {
            setStats(prev => ({
              ...prev,
              friends: socialResponse.data.friends_count || 0,
              following: socialResponse.data.following_count || 0,
              fans: socialResponse.data.fans_count || 0
            }));
          }

          // Feb 2026 redesign: hydrate Top-8 grid (auto-fills from friends
          // when the user hasn't pinned any).
          try {
            const top8Res = await axios.get(`${API_URL}/api/users/me/top8`, { withCredentials: true });
            if (top8Res.data) {
              setTop8Cards(top8Res.data.top8 || []);
              setTop8Ids(top8Res.data.picked_ids || []);
              setTop8IsAuto(!!top8Res.data.is_auto);
            }
          } catch (e) {
            // Non-fatal: profile still renders without Top-8.
            console.log('Top-8 fetch failed (non-fatal):', e?.message);
          }
          
          const response = await axios.get(`${API_URL}/api/posts/user/${currentUser.id}`, {
            withCredentials: true
          });
          const backendPosts = (response.data.posts || []).map(post => ({
            id: post.id,
            type: post.post_type || 'text',
            content: post.content,
            thumbnail: post.media_url,
            likes: post.likes || 0,
            liked_by: post.liked_by || [],
            comments: post.comments_count || 0,
            timestamp: new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            views: post.views || 0,
            raw: post,   // Round 34: the body (Table Talk card / link preview) renders from the full post
          }));
          setPosts(backendPosts);
        } catch (error) {
          console.error('Failed to fetch posts:', error);
          setPosts([]);
        }
      }
    };
    
    loadSavedData();
  }, [currentUser?.id, isOwner]);
  // VISITOR: load the target user's PUBLIC profile by handle (read-only).
  // Keyed on viewHandle only so it fetches once per profile viewed.
  useEffect(() => {
    if (isOwner || !viewHandle) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/handle/${encodeURIComponent(viewHandle)}`, { withCredentials: true });
        const d = res.data;
        if (cancelled) return;
        if (!d || !d.id) { toast.error('Profile not found'); return; }
        setViewedUser(d);
        setIsFollowing(d.relationship === 'following' || d.relationship === 'friends');
        setProfileData(prev => ({
          ...prev,
          name: d.name || prev.name,
          gemsTag: d.handle || prev.gemsTag,
          bio: d.bio || '',
          location: d.location || d.city || '',
          gender: d.gender || '',
          zodiacSign: d.zodiac_sign || '',
          verifiedBadgeType: d.badge_type || null,
          // SEP 3 2026: REAL level from the server (was a hardcoded 42
          // placeholder that never matched mobile). Same formula as the
          // backend fallback: level, else wins-based.
          level: d.level || Math.max(1, Math.floor((d.wins || 0) / 10) + 1),
        }));
        if (d.picture) setProfileImage(d.picture);
        if (d.profile_photos) setProfilePhotos(d.profile_photos);
        if (d.background_image !== undefined) setBackgroundImage(d.background_image || null);
        if (d.background_style) setBackgroundStyle(d.background_style);
        if (typeof d.background_overlay === 'number') setBackgroundOverlay(d.background_overlay);
        if (d.anthem !== undefined) setAnthem(d.anthem || null);
        setStats(prev => ({
          ...prev,
          friends: d.friends_count || 0,
          following: d.following_count || 0,
          fans: d.followers_count || 0,
          gamesWon: d.wins || 0,
          gamesLost: d.losses || 0,
          winStreak: d.streak || 0,
          winRate: d.win_rate || 0,
          emeraldsSpent: d.emeralds_spent || 0,
          rubiesEarned: d.rubies_earned || 0,
        }));
        try {
          const t = await axios.get(`${API_URL}/api/users/${d.id}/top8`, { withCredentials: true });
          if (!cancelled && t.data) {
            setTop8Cards(t.data.top8 || []);
            setTop8Ids(t.data.picked_ids || []);
            setTop8IsAuto(!!t.data.is_auto);
          }
        } catch (e) {
          // Non-fatal: keep any previously-rendered grid.
          console.warn('Top-8 fetch failed (visitor view, non-fatal):', e?.message);
        }
        try {
          const p = await axios.get(`${API_URL}/api/posts/user/${d.id}`, { withCredentials: true });
          if (!cancelled && p.data?.posts) {
            setPosts(p.data.posts.map(post => ({
              id: post.id,
              type: post.post_type || 'text',
              content: post.content,
              thumbnail: post.media_url,
              likes: post.likes || 0,
              liked_by: post.liked_by || [],
              comments: post.comments_count || 0,
              timestamp: new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              views: post.views || 0,
              raw: post,   // Round 34
            })));
          }
        } catch (e) { /* non-fatal */ }
      } catch (e) {
        if (!cancelled) { console.error('Visitor profile load failed:', e); toast.error('Could not load this profile'); }
      }
    })();
    return () => { cancelled = true; };
  }, [viewHandle, isOwner]);
  // Paid Rose Quartz like from a visitor's view of someone else's profile.
  // Owner can't like their own post (backend also enforces).
  const handleLikePost = async (postId) => {
    if (isOwner) return;
    const target = (posts || []).find(p => p.id === postId);
    if (target && (target.liked_by || []).includes(loggedInUser?.id)) return;
    try {
      const res = await axios.post(`${API_URL}/api/posts/${postId}/like`, {}, { withCredentials: true });
      const d = res.data || {};
      setPosts(prev => prev.map(p => p.id === postId
        ? { ...p, likes: (typeof d.likes === 'number' ? d.likes : (p.likes || 0) + 1), liked_by: [...(p.liked_by || []), loggedInUser?.id] }
        : p));
      if (typeof d.emeralds === 'number' && authContext?.setUser) {
        authContext.setUser(prev => (prev ? { ...prev, emeralds: d.emeralds } : prev));
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 402) toast.error("You can't like this — a like costs 1 Emerald and you're out.", { action: { label: 'Get Emeralds', onClick: () => navigate('/wallet') } });
      else if (status === 400) toast.info("You can't like your own post.");
      else toast.error('Failed to like post');
    }
  };

  // ── Profile photo likes (paid Rose Quartz) ──────────────────
  useEffect(() => {
    const uid = currentUser?.id;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/${uid}/photos/likes`, { withCredentials: true });
        if (!cancelled && res.data?.likes) setPhotoLikes(res.data.likes);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  // Location auto-detect: browser geoloc → reverse geocode → 2dp coords + display.
  useEffect(() => {
    if (!isEditing || !locationAuto) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationAuto(false);
      setLocationNote('This browser does not support location — type your location instead.');
      return;
    }
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (cancelled) return;
        const lat = Math.round(pos.coords.latitude * 100) / 100;
        const lng = Math.round(pos.coords.longitude * 100) / 100;
        setLocationCoords({ lat, lng });
        setLocationNote('');
        try {
          const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`);
          const d = await r.json();
          const city = d.city || d.locality || d.principalSubdivision || d.countryName || '';
          const region = (d.principalSubdivisionCode || '').split('-').pop() || '';
          const name = region ? `${city}, ${region}` : city;
          if (!cancelled && name) setProfileData(prev => ({ ...prev, location: name }));
        } catch { /* leave location text as-is */ }
      },
      (err) => {
        if (cancelled) return;
        setLocationAuto(false);
        setLocationCoords(null);
        setLocationNote('Location access denied — type your location instead.');
      },
      { enableHighAccuracy: false, timeout: 8000 }
    );
    return () => { cancelled = true; };
  }, [isEditing, locationAuto]);

  // Manual: debounced suggestion search
  useEffect(() => {
    if (!isEditing || locationAuto) { setLocationSuggestions([]); return; }
    const q = (profileData.location || '').trim();
    if (q.length < 2) { setLocationSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/geo/search`, { params: { q }, withCredentials: true });
        if (!cancelled) setLocationSuggestions(res.data?.results || []);
      } catch { if (!cancelled) setLocationSuggestions([]); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [profileData.location, locationAuto, isEditing]);

  const handleLikePhoto = async (index) => {
    if (isOwner) return;
    const cur = photoLikes[String(index)] || {};
    if (cur.liked) return;
    const ownerId = currentUser?.id;
    if (!ownerId) return;
    // Optimistic — instant, no popup/confirmation.
    setPhotoLikes(prev => ({ ...prev, [String(index)]: { likes: (prev[String(index)]?.likes || 0) + 1, liked: true } }));
    try {
      const res = await axios.post(`${API_URL}/api/users/${ownerId}/photos/${index}/like`, {}, { withCredentials: true });
      const d = res.data || {};
      setPhotoLikes(prev => ({ ...prev, [String(index)]: { likes: (typeof d.likes === 'number' ? d.likes : (prev[String(index)]?.likes || 1)), liked: true } }));
      if (typeof d.emeralds === 'number' && authContext?.setUser) {
        authContext.setUser(prev => (prev ? { ...prev, emeralds: d.emeralds } : prev));
      }
    } catch (error) {
      setPhotoLikes(prev => ({ ...prev, [String(index)]: { likes: Math.max(0, (prev[String(index)]?.likes || 1) - 1), liked: false } }));
      const status = error.response?.status;
      if (status === 402) toast.error('Not enough Emeralds', { action: { label: 'Get Emeralds', onClick: () => navigate('/wallet') } });
      else if (status !== 400) toast.error('Failed to like photo');
    }
  };

  // Save profile to backend
  const saveProfile = async () => {
    try {
      // Save to backend
      const response = await axios.put(`${API_URL}/api/users/profile`, {
        name: profileData.name,
        bio: profileData.bio,
        location: profileData.location,
        location_mode: locationAuto ? 'auto' : 'manual',
        location_coords: locationCoords,
        gender: profileData.gender,
        zodiac_sign: profileData.zodiacSign,
        handle: handleChanged ? undefined : profileData.gemsTag  // Only send handle if not changed before
      }, {
        withCredentials: true
      });
      
      // If handle was just changed, update the state
      if (response.data.user?.handle_changed) {
        setHandleChanged(true);
      }
      
      // Backend is the single source of truth — no localStorage backup
      // (would cause stale reads across devices).
      
      setIsEditing(false);
      toast.success('Profile saved!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save profile';
      toast.error(errorMessage);
    }
  };

  // ══════════════════════════════════════════════════════════════
  // FEB 2026 REDESIGN — new handlers
  // ══════════════════════════════════════════════════════════════

  // Open the picker: snapshot the currently-saved values into the
  // draft so Cancel can revert to them.
  const openBackgroundModal = () => {
    setDraftBgImage(backgroundImage);
    setDraftBgStyle(backgroundStyle);
    setDraftBgOverlay(backgroundOverlay);
    setShowBackgroundModal(true);
  };

  // Cancel: throw away any draft changes and close.
  const cancelBackground = () => {
    setDraftBgImage(backgroundImage);
    setDraftBgStyle(backgroundStyle);
    setDraftBgOverlay(backgroundOverlay);
    setShowBackgroundModal(false);
  };

  // Upload a page-background image via the shared /media/upload endpoint.
  // Feb 2026: this now only updates the DRAFT preview — nothing is
  // persisted until the user hits Save.
  const handleUploadBackground = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploadingBackground(true);
    try {
      // Downscale + compress in the browser, then store the image as a
      // base64 data URL on the user record (same durable approach as
      // profile photos). No ephemeral disk, no static-file URL to 404 —
      // it survives deploys and applies instantly.
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.onload = () => {
          const img = new Image();
          img.onerror = () => reject(new Error('Could not load image'));
          img.onload = () => {
            const MAX = 1600;
            let { width, height } = img;
            if (width > MAX || height > MAX) {
              const scale = Math.min(MAX / width, MAX / height);
              width = Math.round(width * scale);
              height = Math.round(height * scale);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.82));
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
      setDraftBgImage(dataUrl);
      toast.success('Loaded — hit Save to apply');
    } catch (err) {
      console.error('Background load failed:', err);
      toast.error('Failed to load background image');
    } finally {
      setUploadingBackground(false);
    }
  };

  // Save: persist the draft, then commit into live state and close.
  const saveBackground = async () => {
    setSavingBackground(true);
    try {
      await axios.put(`${API_URL}/api/users/profile`, {
        background_image: draftBgImage || '',
        background_style: draftBgStyle,
        background_overlay: draftBgOverlay,
      }, { withCredentials: true });
      setBackgroundImage(draftBgImage || null);
      setBackgroundStyle(draftBgStyle);
      setBackgroundOverlay(draftBgOverlay);
      setShowBackgroundModal(false);
      toast.success('Background saved');
    } catch (err) {
      console.error('Failed to save background:', err);
      toast.error(err.response?.data?.detail || 'Failed to save background');
    } finally {
      setSavingBackground(false);
    }
  };

  const handleClearBackground = async () => {
    // Clears the DRAFT — user still needs to Save to commit.
    setDraftBgImage(null);
    toast.info('Background cleared in preview — hit Save to apply');
  };

  // Persist a new Top-8 order (array of friend user_ids).
  const persistTop8 = async (nextIds) => {
    try {
      const res = await axios.put(`${API_URL}/api/users/profile`, {
        top8: nextIds,
      }, { withCredentials: true });
      // Refetch the hydrated cards so LIVE flags stay fresh.
      const top8Res = await axios.get(`${API_URL}/api/users/me/top8`, { withCredentials: true });
      if (top8Res.data) {
        setTop8Cards(top8Res.data.top8 || []);
        setTop8Ids(top8Res.data.picked_ids || []);
        setTop8IsAuto(!!top8Res.data.is_auto);
      }
      return true;
    } catch (err) {
      console.error('Failed to save Top-8:', err);
      toast.error(err.response?.data?.detail || 'Failed to save Top 8');
      return false;
    }
  };

  // Load the full friends list for the Top-8 view-all/search modal.
  const openTop8Modal = async () => {
    if (!isOwner) return; // round 7: visitors can never open the pin modal
    setShowTop8Modal(true);
    setTop8Search('');
    setPickingReplaceSlot(null);
    setPendingPickFriend(null);
    setLoadingAllFriends(true);
    try {
      const res = await axios.get(`${API_URL}/api/users/me/friends`, { withCredentials: true });
      setAllFriendsForTop8(res.data?.friends || []);
    } catch (err) {
      console.error('Failed to load friends for Top-8:', err);
      setAllFriendsForTop8([]);
    } finally {
      setLoadingAllFriends(false);
    }
  };

  // Called when a friend tile in the modal is tapped.
  const handlePickTop8 = async (friend) => {
    // Already pinned? Unpin instead.
    if (top8Ids.includes(friend.id)) {
      const next = top8Ids.filter(id => id !== friend.id);
      await persistTop8(next);
      return;
    }
    // Fewer than 8: append.
    if (top8Ids.length < 8) {
      const next = [...top8Ids.filter(id => id !== friend.id), friend.id].slice(0, 8);
      await persistTop8(next);
      return;
    }
    // 8 filled → prompt to pick which slot to replace.
    setPendingPickFriend(friend);
    setPickingReplaceSlot(1);
  };

  const confirmReplaceTop8Slot = async (slotIndex) => {
    if (!pendingPickFriend) return;
    const next = [...top8Ids];
    next[slotIndex] = pendingPickFriend.id;
    const ok = await persistTop8(next);
    if (ok) {
      setPendingPickFriend(null);
      setPickingReplaceSlot(null);
    }
  };

  // Slideshow auto-rotate — pauses when hovered / lightbox open.
  useEffect(() => {
    if (profilePhotos.length <= 1) return;
    if (slideshowPaused || showLightbox) return;
    const t = setInterval(() => {
      setSlideshowIndex(prev => (prev + 1) % profilePhotos.length);
    }, 4000);
    return () => clearInterval(t);
  }, [profilePhotos.length, slideshowPaused, showLightbox]);

  // Reset slideshow index when photos change.
  useEffect(() => {
    if (slideshowIndex >= profilePhotos.length) setSlideshowIndex(0);
  }, [profilePhotos.length, slideshowIndex]);

  // Filter friends by the search box in the Top-8 modal.
  const filteredFriendsForTop8 = useMemo(() => {
    const q = top8Search.trim().toLowerCase();
    if (!q) return allFriendsForTop8;
    return allFriendsForTop8.filter(f =>
      (f.name || '').toLowerCase().includes(q) ||
      (f.handle || '').toLowerCase().includes(q)
    );
  }, [allFriendsForTop8, top8Search]);

  // ══════════════════════════════════════════════════════════════
  // ANTHEM PLAYER (Feb 2026)
  // ══════════════════════════════════════════════════════════════
  // Fetch the catalog once when the picker is opened.
  const openAnthemPicker = useCallback(async () => {
    setShowAnthemPicker(true);
    if (anthemCatalog.length === 0) {
      try {
        const res = await axios.get(`${API_URL}/api/anthems`);
        setAnthemCatalog(res.data?.anthems || []);
        if (res.data?.genres?.length) setAnthemGenres(res.data.genres);
      } catch (e) {
        console.error('Failed to load anthem catalog:', e);
        toast.error('Could not load the anthem library');
      }
    }
  }, [anthemCatalog.length]);

  // Persist a picked anthem (or clear when passed null).
  const saveAnthemChoice = useCallback(async (track) => {
    setSavingAnthem(true);
    try {
      await axios.put(`${API_URL}/api/users/profile`, {
        anthem_id: track ? track.id : '',
      }, { withCredentials: true });
      setAnthem(track || null);
      // Reset the main player so it re-loads the new src (or stops if
      // cleared). The <audio> element's src is driven off `anthem.url`.
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) {}
        audioRef.current.currentTime = 0;
      }
      setAnthemPlaying(false);
      toast.success(track ? `Anthem set: ${track.title}` : 'Anthem cleared');
      setShowAnthemPicker(false);
    } catch (err) {
      console.error('Failed to save anthem:', err);
      toast.error(err.response?.data?.detail || 'Failed to save anthem');
    } finally {
      setSavingAnthem(false);
    }
  }, []);

  // Toggle the main pill player. Because browsers block sound-on
  // autoplay, the very first "play" click also un-mutes.
  const toggleAnthemPlayback = useCallback(() => {
    const el = audioRef.current;
    if (!el || !anthem) return;
    // Belt-and-braces: rehydrate src if the browser cleared it after
    // a failed muted-autoplay attempt.
    if (anthem.url && el.src !== anthem.url) {
      el.src = anthem.url;
      el.loop = true;
    }
    if (el.paused) {
      // Ensure the user hears the anthem — the first user gesture
      // grants audio permission, so we unmute right here.
      el.muted = false;
      setAnthemMuted(false);
      el.play().then(() => setAnthemPlaying(true)).catch((err) => {
        console.warn('Anthem play blocked:', err);
        toast.info('Tap once more to allow audio');
      });
    } else {
      el.pause();
      setAnthemPlaying(false);
    }
  }, [anthem]);

  const toggleAnthemMute = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setAnthemMuted(el.muted);
  }, []);

  // Preview a track from inside the picker WITHOUT saving it. Only
  // one preview plays at a time — clicking a second one interrupts.
  const togglePreview = useCallback((track) => {
    const el = previewAudioRef.current;
    if (!el) return;
    if (previewAnthemId === track.id && !el.paused) {
      el.pause();
      setPreviewAnthemId(null);
      return;
    }
    el.src = track.url;
    el.currentTime = 0;
    el.muted = false;
    el.play().then(() => setPreviewAnthemId(track.id)).catch((err) => {
      console.warn('Preview play blocked:', err);
    });
  }, [previewAnthemId]);

  // Keep the main player src in sync with the selected anthem.
  // Autoplay policy: attempt a muted autoplay so visitors see the
  // equalizer bars animate immediately; a first tap on the pill
  // unmutes and unpauses (see toggleAnthemPlayback above).
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !anthem?.url) return;
    if (el.getAttribute('data-current-src') === anthem.url) return;
    el.setAttribute('data-current-src', anthem.url);
    el.src = anthem.url;
    el.loop = true;
    el.muted = true;
    setAnthemMuted(true);
    el.play().then(() => setAnthemPlaying(true)).catch(() => {
      // Muted autoplay was blocked (some mobile browsers) — wait for
      // the user to tap the pill. The UI shows a "tap to play" hint
      // via the play icon on the pill.
      setAnthemPlaying(false);
    });
  }, [anthem?.url]);


  
  // Fetch social list (friends, following, fans)
  const fetchSocialList = async (type) => {
    setLoadingSocial(true);
    setSocialListType(type);
    setShowSocialModal(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/users/social/${type}`, {
        withCredentials: true
      });
      setSocialListData(response.data.users || []);
    } catch (error) {
      console.error(`Failed to fetch ${type}:`, error);
      setSocialListData([]);
    } finally {
      setLoadingSocial(false);
    }
  };

  // Follow/unfollow the profile you're viewing (visitor mode).
  const handleFollowToggle = async () => {
    if (!viewedUser?.id) return;
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing); // optimistic
    try {
      const endpoint = wasFollowing ? 'unfollow' : 'follow';
      await axios.post(`${API_URL}/api/users/${endpoint}`, { user_id: viewedUser.id }, { withCredentials: true });
      setStats(prev => ({ ...prev, fans: Math.max(0, (prev.fans || 0) + (wasFollowing ? -1 : 1)) }));
      toast.success(wasFollowing ? 'Unfollowed' : 'Followed');
    } catch (e) {
      setIsFollowing(wasFollowing); // revert on failure
      toast.error('Could not update follow');
    }
  };
  // Handle follow/unfollow from social modal
  const handleSocialAction = async (targetId, currentRelationship) => {
    try {
      if (currentRelationship === 'following' || currentRelationship === 'friends') {
        await axios.post(`${API_URL}/api/users/unfollow`, { user_id: targetId }, { withCredentials: true });
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API_URL}/api/users/follow`, { user_id: targetId }, { withCredentials: true });
        toast.success('Followed');
      }
      // Refresh the list and stats
      fetchSocialList(socialListType);
      const socialResponse = await axios.get(`${API_URL}/api/users/social/stats`, { withCredentials: true });
      if (socialResponse.data) {
        setStats(prev => ({
          ...prev,
          friends: socialResponse.data.friends_count || 0,
          following: socialResponse.data.following_count || 0,
          fans: socialResponse.data.fans_count || 0
        }));
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };
  
  // Handle image uploads
  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (type === 'profile') {
          setProfileImage(e.target.result);
          // Sync to backend (single source of truth — no localStorage backup)
          try {
            await axios.put(`${API_URL}/api/users/profile`, { picture: e.target.result }, { withCredentials: true });
            // Feb 2026: also push the new picture into AuthContext so the
            // NavBar avatar in the global header refreshes to the same
            // image immediately (no page reload needed).
            if (authContext?.setUser) {
              authContext.setUser(prev => (prev ? { ...prev, picture: e.target.result } : prev));
            }
          } catch (err) {
            console.error('Failed to sync profile image:', err);
          }
        } else if (type === 'cover') {
          setCoverImage(e.target.result);
          try {
            await axios.put(`${API_URL}/api/users/profile`, { cover_image: e.target.result }, { withCredentials: true });
            if (authContext?.setUser) {
              authContext.setUser(prev => (prev ? { ...prev, cover_image: e.target.result } : prev));
            }
          } catch (err) {
            console.error('Failed to sync cover image:', err);
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle profile photo gallery upload (6 photos)
  const handlePhotoGalleryUpload = async (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const newPhotos = [...profilePhotos];
        newPhotos[index] = e.target.result;
        setProfilePhotos(newPhotos);
        
        // Sync to backend
        try {
          await axios.put(`${API_URL}/api/users/profile`, { 
            profile_photos: newPhotos 
          }, { withCredentials: true });
          toast.success('Photo added to gallery!');
        } catch (err) {
          console.error('Failed to sync photo:', err);
          toast.error('Failed to save photo');
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const removePhotoFromGallery = async (index) => {
    const newPhotos = profilePhotos.filter((_, i) => i !== index);
    setProfilePhotos(newPhotos);
    
    try {
      await axios.put(`${API_URL}/api/users/profile`, { 
        profile_photos: newPhotos 
      }, { withCredentials: true });
      toast.success('Photo removed');
    } catch (err) {
      console.error('Failed to remove photo:', err);
    }
  };
  
  // Navigate photo gallery
  const nextPhoto = () => {
    if (profilePhotos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev + 1) % profilePhotos.length);
    }
  };
  
  const prevPhoto = () => {
    if (profilePhotos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev - 1 + profilePhotos.length) % profilePhotos.length);
    }
  };
  
  // Handle new post - saves to backend and appears on Discover page
  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !newPostMedia) {
      toast.error('Please add some content');
      return;
    }
    
    try {
      // Create post via API
      const response = await axios.post(`${API_URL}/api/posts`, {
        content: newPostContent,
        post_type: newPostMediaType || 'text',
        media_url: newPostMedia,
        thumbnail_url: newPostMedia
      }, {
        withCredentials: true
      });
      
      if (response.data.post) {
        // Add to local state for immediate UI update
        const backendPost = response.data.post;
        const newPost = {
          id: backendPost.id,
          type: backendPost.post_type,
          content: backendPost.content,
          thumbnail: backendPost.media_url,
          likes: 0,
          comments: 0,
          timestamp: 'Just now',
          views: 0
        };
        
        const updatedPosts = [newPost, ...posts];
        setPosts(updatedPosts);
        
        toast.success('Post created! It will appear on the Discover page.');
      }
    } catch (error) {
      console.error('Failed to create post:', error);
      
      // Fallback to local storage if API fails
      const newPost = {
        id: Date.now().toString(),
        type: newPostMediaType || 'text',
        content: newPostContent,
        thumbnail: newPostMedia,
        likes: 0,
        comments: 0,
        timestamp: 'Just now'
      };
      
      const updatedPosts = [newPost, ...posts];
      setPosts(updatedPosts);
      toast.success('Post saved locally!');
    }
    
    setNewPostContent('');
    setNewPostMedia(null);
    setNewPostMediaType(null);
    setShowPostModal(false);
  };
  
  // Delete post function
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/posts/${postId}`, {
        withCredentials: true
      });
      
      // Remove from local state
      const updatedPosts = posts.filter(p => p.id !== postId);
      setPosts(updatedPosts);
      
      toast.success('Post deleted successfully');
    } catch (error) {
      console.error('Failed to delete post:', error);
      
      // Still remove from local state if API fails
      const updatedPosts = posts.filter(p => p.id !== postId);
      setPosts(updatedPosts);
      
      toast.success('Post removed');
    }
  };
  
  // Handle post media upload
  const handlePostMediaUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewPostMedia(e.target.result);
        setNewPostMediaType(file.type.startsWith('video') ? 'video' : 'image');
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Share functions
  const shareToFriends = async () => {
    setShowShareModal(false);
    setShowShareToFriendsModal(true);
    setLoadingFriends(true);
    
    try {
      // Fetch friends, followers, and fans
      const [friendsRes, followersRes, fansRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/social/friends`).catch(() => ({ data: { users: [] } })),
        axios.get(`${API_URL}/api/users/social/following`).catch(() => ({ data: { users: [] } })),
        axios.get(`${API_URL}/api/users/social/fans`).catch(() => ({ data: { users: [] } }))
      ]);
      
      // Combine and deduplicate
      const allUsers = [
        ...(friendsRes.data.users || []),
        ...(followersRes.data.users || []),
        ...(fansRes.data.users || [])
      ];
      
      const uniqueUsers = allUsers.reduce((acc, user) => {
        if (!acc.find(u => u.id === user.id)) {
          acc.push(user);
        }
        return acc;
      }, []);
      
      setFriendsToShare(uniqueUsers);
    } catch (error) {
      console.error('Failed to load friends:', error);
      toast.error('Failed to load friends');
    } finally {
      setLoadingFriends(false);
    }
  };
  
  const sendShareToFriends = async () => {
    if (selectedFriendsToShare.length === 0) {
      toast.error('Please select at least one friend');
      return;
    }
    
    try {
      const profileUrl = `${window.location.origin}/profile/${profileData.gemsTag}`;
      const message = `${shareMessage}\n\n${profileUrl}`;
      
      // Send message to each selected friend
      for (const friendId of selectedFriendsToShare) {
        await axios.post(`${API_URL}/api/messages/send`, {
          recipient_id: friendId,
          content: message
        });
      }
      
      toast.success(`Profile shared with ${selectedFriendsToShare.length} friend${selectedFriendsToShare.length > 1 ? 's' : ''}!`);
      setShowShareToFriendsModal(false);
      setSelectedFriendsToShare([]);
      setShareMessage('Check out my GEMS Live profile!');
    } catch (error) {
      console.error('Failed to share:', error);
      toast.error('Failed to share profile');
    }
  };
  
  const toggleFriendSelection = (friendId) => {
    setSelectedFriendsToShare(prev => 
      prev.includes(friendId) 
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };
  
  const shareToWhatsApp = () => {
    const url = `${window.location.origin}/profile/${profileData.gemsTag}`;
    window.open(`https://wa.me/?text=Check out my GEMS Live profile! ${url}`, '_blank');
    setShowShareModal(false);
  };
  
  const shareToSMS = () => {
    const url = `${window.location.origin}/profile/${profileData.gemsTag}`;
    window.open(`sms:?body=Check out my GEMS Live profile! ${url}`, '_blank');
    setShowShareModal(false);
  };
  
  const copyProfileLink = () => {
    const url = `${window.location.origin}/profile/${profileData.gemsTag}`;
    navigator.clipboard.writeText(url);
    toast.success('Profile link copied!');
    setShowShareModal(false);
  };
  
  // Help & Support Functions
  const fetchMyTickets = async () => {
    setLoadingTickets(true);
    try {
      const response = await axios.get(`${API_URL}/api/support/tickets`, { withCredentials: true });
      setMyTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };
  
  const submitSupportTicket = async () => {
    if (!helpSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!helpCategory) {
      toast.error('Please select a category');
      return;
    }
    if (!helpMessage.trim()) {
      toast.error('Please describe your issue');
      return;
    }
    
    setSubmittingTicket(true);
    try {
      await axios.post(`${API_URL}/api/support/tickets`, {
        subject: helpSubject.trim(),
        category: helpCategory,
        message: helpMessage.trim(),
        attachment_url: helpAttachment
      }, { withCredentials: true });
      
      toast.success('Support ticket submitted successfully!');
      setHelpSubject('');
      setHelpCategory('');
      setHelpMessage('');
      setHelpAttachment(null);
      setHelpTab('tickets');
      fetchMyTickets();
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };
  
  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'in_progress': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'resolved': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'closed': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };
  
  const formatTicketDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  
  // Get badge display - badges that can be earned/assigned later
  const getBadgeDisplay = (badge) => {
    const badges = {
      // Future badges - commented out for now
      // verified: { icon: '✓', color: 'bg-blue-500', label: 'Verified' },
      // top_streamer: { icon: '🎬', color: 'bg-red-500', label: 'Top Streamer' },
      beta_tester: { icon: 'β', color: 'bg-purple-500', label: 'Beta Tester' },
      vip: { icon: '⭐', color: 'bg-amber-500', label: 'VIP' },
      partner: { icon: '🤝', color: 'bg-emerald-500', label: 'Partner' }
    };
    return badges[badge] || { icon: '?', color: 'bg-gray-500', label: badge };
  };

  return (
    <div
      className="min-h-screen relative"
      style={{ backgroundColor: '#0A0F1A' }}
      data-testid="profile-page-root"
    >
      {/* ═══════════════════════════════════════════════════════════
          FULL-PAGE CUSTOM BACKGROUND (Feb 2026 redesign)
          Renders the user's uploaded image behind everything with
          the chosen style (Fill/Fit/Tile/Center/Blur) and a dark
          scrim controlled by background_overlay. Fixed to the
          viewport so it doesn't scroll away.
          ═══════════════════════════════════════════════════════════ */}
      {backgroundImage ? (
        <div
          aria-hidden="true"
          data-testid="profile-background-layer"
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: backgroundStyle === 'contain'
              ? 'contain'
              : backgroundStyle === 'repeat'
                ? '150px'  // Feb 2026: smaller tiles so pattern is visible
                : backgroundStyle === 'center'
                  ? 'auto'
                  : 'cover',
            backgroundRepeat: backgroundStyle === 'repeat' ? 'repeat' : 'no-repeat',
            backgroundPosition: 'center',
            filter: backgroundStyle === 'blur' ? 'blur(24px) saturate(120%)' : 'none',
            transform: backgroundStyle === 'blur' ? 'scale(1.05)' : 'none',
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            background:
              'radial-gradient(1200px 600px at 15% -10%, rgba(52,227,164,0.14), transparent 60%), radial-gradient(1000px 500px at 90% 20%, rgba(230,195,74,0.08), transparent 60%), #0A0F1A',
          }}
        />
      )}
      {/* Dark scrim over the background for text readability */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none z-0"
        style={{ backgroundColor: `rgba(6, 10, 18, ${backgroundOverlay})` }}
      />

      {/* Global top header — HARD RULE: never touch the NavBar internals.
          The <nav> already owns `sticky top-0 z-50`. Do NOT wrap it in a
          positioned/z-indexed div — a wrapper only as tall as the nav
          breaks position:sticky (nothing to stick within) AND traps the
          avatar dropdown beneath the z-10 content column below. Render
          it directly so it stays pinned and its z-50 sits above content. */}
      {NavBar && <NavBar />}

      {/* ═══════════════════════════════════════════════════════════
          IDENTITY HEADER — avatar, name/handle/location/level, bio,
          song pill placeholder, Edit / Share / Help buttons.
          Avatar sits inside the same content column as the identity
          row so it stays anchored on scroll/resize (previous bug
          was from a negative-margin overlap on a floating cover).
          ═══════════════════════════════════════════════════════════ */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 pt-8 md:pt-12">
        {/* Change-background control — floats top-right of the content column */}
        {isOwner && (
        <div className="flex justify-end mb-4">
          <Button
            onClick={openBackgroundModal}
            variant="outline"
            size="sm"
            className="bg-black/40 hover:bg-black/60 border-white/20 text-white backdrop-blur-md"
            data-testid="change-background-btn"
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            Change page background
          </Button>
        </div>
        )}

        <div
          className="rounded-[22px] border border-white/10 backdrop-blur-[14px] p-5 md:p-7"
          style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
          data-testid="profile-identity-card"
        >
          <div className="flex flex-col md:flex-row items-start gap-5 md:gap-7">
            {/* Avatar — rounded-square, ring, online dot, camera badge */}
            <div className="relative shrink-0" data-testid="profile-avatar">
              <div
                className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden shadow-xl"
                style={{
                  boxShadow: '0 0 0 3px rgba(52,227,164,0.7), 0 12px 40px rgba(0,0,0,0.5)',
                  backgroundColor: '#0e1420',
                }}
              >
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#34E3A4,#22C98C)' }}>
                    <Users className="w-14 h-14 text-white/90" />
                  </div>
                )}
              </div>
              {/* Online dot */}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2" style={{ backgroundColor: '#22C98C', borderColor: '#0A0F1A' }} />
              {/* Camera badge (avatar upload) */}
              {isOwner && (
              <label className="absolute -top-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer shadow-lg" style={{ backgroundColor: '#34E3A4', color: '#062015' }} data-testid="avatar-upload-btn">
                <Camera className="w-4 h-4" />
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'profile')} className="hidden" />
              </label>
              )}
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white" data-testid="profile-name">{profileData.name}</h1>
                {profileData.verifiedBadgeType === 'sapphire' && <SapphireBadgeIcon className="w-6 h-6" />}
                {(profileData.verifiedBadgeType === 'canary' || profileData.verifiedBadgeType === 'verified') && <CanaryBadgeIcon className="w-6 h-6" />}
                {profileData.badges.map((badge, idx) => {
                  const b = getBadgeDisplay(badge);
                  return (
                    <span key={idx} className={`${b.color} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
                      {b.icon} {b.label}
                    </span>
                  );
                })}
              </div>
              <p className="mt-1 text-lg font-semibold" style={{ color: '#34E3A4' }} data-testid="profile-handle">@{profileData.gemsTag}</p>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5 text-white/85">
                  <MapPin className="w-4 h-4" />
                  {profileData.location || 'Location not set'}
                </span>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold font-mono"
                  style={{ backgroundColor: 'rgba(230,195,74,0.15)', color: '#E6C34A', border: '1px solid rgba(230,195,74,0.35)' }}
                  data-testid="profile-level-chip"
                >
                  <Crown className="w-3.5 h-3.5" />
                  LVL {profileData.level}
                </span>
              </div>

              {profileData.bio && (
                <p className="mt-3 text-white/90 max-w-2xl leading-relaxed" data-testid="profile-bio">{profileData.bio}</p>
              )}

              {/* Song / anthem pill (Feb 2026 — real player).
                  - When no anthem picked: pill opens the picker.
                  - When an anthem is set: pill plays/pauses/mutes the
                    real <audio>, and secondary tap opens the picker
                    via the "change" affordance. */}
              {anthem ? (
                <div
                  className="mt-4 inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full text-sm border border-white/15"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                  data-testid="anthem-pill"
                >
                  <button
                    onClick={toggleAnthemPlayback}
                    className="flex items-center gap-1.5"
                    aria-label={anthemPlaying ? 'Pause anthem' : 'Play anthem'}
                    data-testid="anthem-play-toggle"
                  >
                    {anthemPlaying ? (
                      <Pause className="w-4 h-4" style={{ color: '#34E3A4' }} />
                    ) : (
                      <Play className="w-4 h-4" style={{ color: '#34E3A4' }} />
                    )}
                  </button>
                  <Music className="w-3.5 h-3.5 text-white/60" />
                  <span className="text-white/90 max-w-[180px] truncate" title={`${anthem.title} — ${anthem.artist}`}>
                    <span className="font-medium">{anthem.title}</span>
                    <span className="text-white/50"> · {anthem.artist}</span>
                  </span>
                  {/* Equalizer bars — animate only while playing */}
                  <span className="ml-1 flex items-end gap-0.5 h-3.5">
                    {[0, 1, 2, 3].map(i => (
                      <span
                        key={i}
                        className="w-0.5 rounded-full"
                        style={{
                          backgroundColor: '#34E3A4',
                          height: anthemPlaying ? `${40 + ((i * 23) % 60)}%` : '30%',
                          animation: anthemPlaying ? `anthem-bounce 900ms ${i * 120}ms ease-in-out infinite` : 'none',
                        }}
                      />
                    ))}
                  </span>
                  <button
                    onClick={toggleAnthemMute}
                    className="ml-1 opacity-70 hover:opacity-100"
                    aria-label={anthemMuted ? 'Unmute anthem' : 'Mute anthem'}
                    data-testid="anthem-mute-toggle"
                  >
                    {anthemMuted ? (
                      <VolumeX className="w-4 h-4 text-white/70" />
                    ) : (
                      <Volume2 className="w-4 h-4" style={{ color: '#34E3A4' }} />
                    )}
                  </button>
                  {isOwner && (
                  <button
                    onClick={openAnthemPicker}
                    className="ml-1 text-[10px] uppercase tracking-widest text-white/50 hover:text-white"
                    data-testid="anthem-change-btn"
                  >
                    change
                  </button>
                  )}
                </div>
              ) : isOwner ? (
                <button
                  onClick={openAnthemPicker}
                  className="mt-4 inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full text-sm border border-white/15 hover:bg-white/5 transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                  data-testid="anthem-pill"
                  title="Pick a royalty-free anthem"
                >
                  <Play className="w-4 h-4" style={{ color: '#34E3A4' }} />
                  <Music className="w-3.5 h-3.5 text-white/60" />
                  <span className="text-white/85">Set your anthem</span>
                  <span className="ml-1 flex items-end gap-0.5 h-3.5">
                    {[0, 1, 2, 3].map(i => (
                      <span
                        key={i}
                        className="w-0.5 rounded-full"
                        style={{ backgroundColor: '#34E3A4', height: '30%' }}
                      />
                    ))}
                  </span>
                </button>
              ) : null}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap md:flex-nowrap gap-2 md:self-start">
              {isOwner && (
              <Button
                onClick={() => setIsEditing(!isEditing)}
                style={{ backgroundColor: '#22C98C', color: '#062015' }}
                className="hover:brightness-110 font-bold"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              )}
              {!isOwner && (
              <Button
                onClick={handleFollowToggle}
                className={isFollowing ? "border border-white/20 text-white hover:bg-white/10 backdrop-blur-sm bg-transparent font-semibold" : "font-bold hover:brightness-110"}
                style={isFollowing ? {} : { backgroundColor: '#22C98C', color: '#062015' }}
                data-testid="follow-toggle-btn"
              >
                {isFollowing ? (<><UserCheck className="w-4 h-4 mr-2" />Following</>) : (<><UserPlus className="w-4 h-4 mr-2" />Follow</>)}
              </Button>
              )}
              <Button
                onClick={() => setShowShareModal(true)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 backdrop-blur-sm"
                data-testid="share-profile-btn"
              >
                <Share className="w-4 h-4 mr-2" />
                Share
              </Button>
              {isOwner && (
              <Button
                onClick={() => { setShowHelpModal(true); fetchMyTickets(); }}
                variant="outline"
                className="border-white/20 text-white/85 hover:bg-white/10 backdrop-blur-sm"
                data-testid="help-support-button"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Help
              </Button>
              )}
            </div>
          </div>

          {/* ── STATS BAR — Fans / Following / Friends / Emeralds Spent / Rubies Earned ── */}
          <div className="mt-6 grid grid-cols-3 md:grid-cols-5 gap-3">
            <button
              className="rounded-2xl border border-white/10 p-3 text-left hover:bg-white/5 transition-colors"
              style={{ backgroundColor: 'rgba(10,15,25,0.55)' }}
              onClick={() => fetchSocialList('fans')}
              data-testid="stat-fans"
            >
              <div className="text-2xl font-black font-mono text-white">{stats.fans.toLocaleString()}</div>
              <div className="text-xs uppercase tracking-widest text-white/60 mt-0.5">Fans</div>
            </button>
            <button
              className="rounded-2xl border border-white/10 p-3 text-left hover:bg-white/5 transition-colors"
              style={{ backgroundColor: 'rgba(10,15,25,0.55)' }}
              onClick={() => fetchSocialList('following')}
              data-testid="stat-following"
            >
              <div className="text-2xl font-black font-mono text-white">{stats.following.toLocaleString()}</div>
              <div className="text-xs uppercase tracking-widest text-white/60 mt-0.5">Following</div>
            </button>
            <button
              className="rounded-2xl border border-white/10 p-3 text-left hover:bg-white/5 transition-colors"
              style={{ backgroundColor: 'rgba(10,15,25,0.55)' }}
              onClick={() => fetchSocialList('friends')}
              data-testid="stat-friends"
            >
              <div className="text-2xl font-black font-mono" style={{ color: '#34E3A4' }}>{stats.friends}</div>
              <div className="text-xs uppercase tracking-widest text-white/60 mt-0.5">Friends</div>
            </button>
            <div
              className="rounded-2xl border border-white/10 p-3"
              style={{ backgroundColor: 'rgba(10,15,25,0.55)' }}
              data-testid="stat-emeralds-spent"
            >
              <div className="text-2xl font-black font-mono flex items-center gap-1.5" style={{ color: '#34E3A4' }}>
                <EmeraldIcon className="w-5 h-5" />
                {stats.emeraldsSpent.toLocaleString()}
              </div>
              <div className="text-xs uppercase tracking-widest text-white/60 mt-0.5">Emeralds Spent</div>
            </div>
            <div
              className="rounded-2xl border border-white/10 p-3"
              style={{ backgroundColor: 'rgba(10,15,25,0.55)' }}
              data-testid="stat-rubies-earned"
            >
              <div className="text-2xl font-black font-mono flex items-center gap-1.5" style={{ color: '#FF3B57' }}>
                <RubyIcon className="w-5 h-5" />
                {stats.rubiesEarned.toLocaleString()}
              </div>
              <div className="text-xs uppercase tracking-widest text-white/60 mt-0.5">Rubies Earned</div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          PHOTOS SLIDESHOW (BIGO-style)
          Auto-rotates every 4s, pauses on hover, dots + n/6 counter,
          tap to open a full-screen lightbox. Upload/reorder moves to
          Edit mode (unchanged behavior).
          ═══════════════════════════════════════════════════════════ */}
      {(profilePhotos.length > 0 || isEditing) && (
        <div className="relative z-10 max-w-6xl mx-auto px-4 mt-6">
          <div
            className="rounded-[22px] border border-white/10 backdrop-blur-[14px] p-4 md:p-5"
            style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
            data-testid="photos-slideshow-card"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-black text-white tracking-tight flex items-center gap-2">
                <ImageIcon className="w-5 h-5" style={{ color: '#34E3A4' }} />
                Photos <span className="text-white/50 font-mono text-sm ml-1">{profilePhotos.length}/6</span>
              </h3>
              {isEditing && profilePhotos.length < 6 && (
                <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full cursor-pointer" style={{ backgroundColor: '#22C98C', color: '#062015' }}>
                  <Plus className="w-3.5 h-3.5" />
                  Add
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const nextEmpty = [0,1,2,3,4,5].find(i => !profilePhotos[i]);
                      if (nextEmpty !== undefined) handlePhotoGalleryUpload(e, nextEmpty);
                    }}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {profilePhotos.length === 0 ? (
              <p className="text-white/60 text-sm text-center py-10">No photos yet. Tap "Add" to upload.</p>
            ) : (
              <div
                className="relative rounded-2xl overflow-hidden bg-black/40"
                style={{ aspectRatio: '16 / 9', maxHeight: '520px' }}
                onMouseEnter={() => setSlideshowPaused(true)}
                onMouseLeave={() => setSlideshowPaused(false)}
                data-testid="photos-slideshow"
              >
                {/* Blurred backdrop of the current photo — letterboxed
                    area now looks intentional instead of an empty box.
                    The foreground <img> uses object-contain so the
                    WHOLE photo is always visible, never cropped. */}
                {profilePhotos[slideshowIndex] && (
                  <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url(${profilePhotos[slideshowIndex]})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: 'blur(28px) saturate(120%) brightness(0.6)',
                      transform: 'scale(1.15)',
                    }}
                  />
                )}
                {profilePhotos.map((photo, idx) => (
                  <img
                    key={`${idx}-${photo?.slice?.(0, 32) || idx}`}
                    src={photo}
                    alt={`Photo ${idx + 1}`}
                    className="absolute inset-0 w-full h-full object-contain cursor-pointer transition-opacity duration-700"
                    style={{ opacity: idx === slideshowIndex ? 1 : 0 }}
                    onClick={() => { setLightboxIndex(idx); setShowLightbox(true); }}
                  />
                ))}
                {/* Prev / Next arrows */}
                {profilePhotos.length > 1 && (
                  <>
                    <button
                      onClick={() => setSlideshowIndex(i => (i - 1 + profilePhotos.length) % profilePhotos.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors"
                      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                      aria-label="Previous photo"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setSlideshowIndex(i => (i + 1) % profilePhotos.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors"
                      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                      aria-label="Next photo"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
                {/* Counter */}
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-mono text-white" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                  {slideshowIndex + 1}/{profilePhotos.length}
                </div>
                {/* Rose Quartz like on the current photo */}
                {(() => {
                  const pl = photoLikes[String(slideshowIndex)] || {};
                  return (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleLikePhoto(slideshowIndex); }}
                      disabled={isOwner || pl.liked}
                      className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white"
                      style={{ backgroundColor: 'rgba(0,0,0,0.55)', cursor: (isOwner || pl.liked) ? 'default' : 'pointer' }}
                      title={isOwner ? 'Photo likes' : 'Like this photo'}
                      data-testid={`photo-like-${slideshowIndex}`}
                    >
                      <RoseQuartzIcon className="w-4 h-4" style={{ opacity: pl.liked ? 1 : 0.6 }} />
                      <span className="text-xs font-mono">{pl.likes || 0}</span>
                    </button>
                  );
                })()}
                {/* Dots */}
                {profilePhotos.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {profilePhotos.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSlideshowIndex(idx)}
                        className="w-2 h-2 rounded-full transition-all"
                        style={{
                          backgroundColor: idx === slideshowIndex ? '#34E3A4' : 'rgba(255,255,255,0.5)',
                          width: idx === slideshowIndex ? '16px' : '8px',
                        }}
                        aria-label={`Go to photo ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
                {/* Delete button in Edit mode */}
                {isEditing && profilePhotos[slideshowIndex] && (
                  <button
                    onClick={() => removePhotoFromGallery(slideshowIndex)}
                    className="absolute top-2 left-2 bg-red-500/90 hover:bg-red-500 text-white text-xs px-2 py-1 rounded-full"
                    data-testid="delete-slideshow-photo"
                  >
                    <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-6 mt-6">
        {isEditing ? (
          /* Edit Form */
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-6">Edit Profile</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Name</label>
                  <Input 
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-slate-900/50 border-slate-600 text-white placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">GEMS Tag</label>
                  <Input 
                    value={profileData.gemsTag}
                    onChange={(e) => !handleChanged && setProfileData(prev => ({ ...prev, gemsTag: e.target.value }))}
                    className={`bg-slate-900/50 border-slate-600 text-white placeholder-gray-400 ${handleChanged ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={handleChanged}
                  />
                  {handleChanged && (
                    <p className="text-gray-500 text-xs mt-1">
                      If there&apos;s an issue with your GEMS tag, please contact support.
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-gray-400 text-sm">Location</label>
                    <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none" data-testid="location-auto-toggle">
                      <input
                        type="checkbox"
                        checked={locationAuto}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setLocationAuto(on);
                          setLocationNote('');
                          if (!on) setLocationSuggestions([]);
                        }}
                        className="h-4 w-4 accent-emerald-500"
                      />
                      <span>📍 Use my device location</span>
                    </label>
                  </div>
                  <div className="relative">
                    <Input 
                      value={profileData.location}
                      onChange={(e) => {
                        if (locationAuto) return;
                        setProfileData(prev => ({ ...prev, location: e.target.value }));
                        setLocationCoords(null);  // typed text ≠ picked suggestion until they choose
                      }}
                      readOnly={locationAuto}
                      placeholder={locationAuto ? 'Detecting your city…' : 'Type city and pick a suggestion'}
                      className="bg-slate-900/50 border-slate-600 text-white placeholder-gray-400"
                      data-testid="location-input"
                    />
                    {!locationAuto && locationSuggestions.length > 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#0E141C] border border-white/10 rounded-xl overflow-hidden shadow-2xl" data-testid="location-suggestions">
                        {locationSuggestions.map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              const clean = s.name.split(',').slice(0, 2).join(', ').trim();
                              setProfileData(prev => ({ ...prev, location: clean }));
                              setLocationCoords({ lat: s.lat, lng: s.lng });
                              setLocationSuggestions([]);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10"
                          >
                            {s.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {locationNote && (
                    <p className="text-amber-400 text-xs mt-1">{locationNote}</p>
                  )}
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Age</label>
                  <Input 
                    type="number"
                    value={profileData.age}
                    onChange={(e) => setProfileData(prev => ({ ...prev, age: parseInt(e.target.value) }))}
                    className="bg-slate-900/50 border-slate-600 text-white placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Gender</label>
                  <select 
                    value={profileData.gender}
                    onChange={(e) => setProfileData(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                  >
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Zodiac Sign</label>
                  <select 
                    value={profileData.zodiacSign}
                    onChange={(e) => setProfileData(prev => ({ ...prev, zodiacSign: e.target.value }))}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                  >
                    <option value="">Select Zodiac Sign</option>
                    {ZODIAC_SIGNS.map(z => (
                      <option key={z} value={z}>{z}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 text-sm mb-2">Bio</label>
                  <Textarea 
                    value={profileData.bio}
                    onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                    className="bg-slate-900/50 border-slate-600 text-white placeholder-gray-400"
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={saveProfile} className="bg-emerald-600 hover:bg-emerald-500">
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </Button>
                <Button onClick={() => setIsEditing(false)} variant="outline">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Sidebar */}
            <div className="space-y-6">
              {/* Top 8 Friends (replaces old Friends card, Feb 2026) */}
              <div
                className="rounded-[22px] border border-white/10 backdrop-blur-[14px] p-4"
                style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
                data-testid="top8-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-black text-white tracking-tight flex items-center gap-2">
                    <UserCheck className="w-5 h-5" style={{ color: '#34E3A4' }} />
                    Top 8
                    {top8IsAuto && (
                      <span className="text-[10px] font-normal uppercase tracking-widest text-white/40 ml-1">auto</span>
                    )}
                  </h3>
                  {/* Round 7 FIX: pinning is OWNER-ONLY — this button rendered
                      on visited profiles and silently rewrote the VISITOR'S
                      own Top 8. Visitors see the grid, nothing else. */}
                  {isOwner && (
                    <button
                      onClick={openTop8Modal}
                      className="text-xs font-bold px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: 'rgba(52,227,164,0.14)', color: '#34E3A4', border: '1px solid rgba(52,227,164,0.35)' }}
                      data-testid="top8-view-all-btn"
                    >
                      View all friends
                    </button>
                  )}
                </div>
                {top8Cards.length === 0 ? (
                  <p className="text-white/60 text-sm text-center py-6">{isOwner ? "No friends yet. Once you add some, they'll fill your Top 8 automatically." : 'No Top 8 yet.'}</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2.5">
                    {top8Cards.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => navigate(`/profile/${f.handle || f.id}`)}
                        className="group relative aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-emerald-400/60 transition-all"
                        style={{ backgroundColor: 'rgba(10,15,25,0.55)' }}
                        title={f.name}
                        data-testid={`top8-friend-${f.id}`}
                      >
                        {f.picture ? (
                          <img src={f.picture} alt={f.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white font-black text-lg" style={{ background: 'linear-gradient(135deg,#34E3A4,#22C98C)' }}>
                            {(f.name || '?')[0]}
                          </div>
                        )}
                        {f.is_live && (
                          <span
                            className="absolute top-1 left-1 text-[9px] font-black px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: '#FF3B57', color: 'white' }}
                          >
                            LIVE
                          </span>
                        )}
                        <span className="absolute bottom-0 left-0 right-0 px-1.5 py-1 text-[10px] text-white font-medium truncate bg-black/60">
                          {f.name}
                        </span>
                      </button>
                    ))}
                    {/* Empty slots */}
                    {Array.from({ length: Math.max(0, 8 - top8Cards.length) }).map((_, idx) => (
                      <button
                        key={`empty-${idx}`}
                        onClick={openTop8Modal}
                        className="aspect-square rounded-xl border border-dashed border-white/15 hover:border-emerald-400/40 flex items-center justify-center transition-colors"
                        aria-label="Pick a friend for this slot"
                      >
                        <Plus className="w-4 h-4 text-white/30" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Referral Code Card — owner-only reveal. Public
                  profile (`PublicProfilePage`) never renders this
                  component, and the backend already strips
                  `referral_code` from every non-`/auth/me` response. */}
              {isOwner && (
              <div
                className="rounded-[22px] border border-white/10 backdrop-blur-[14px] p-4"
                style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
                data-testid="profile-referral-card"
              >
                <h3 className="text-lg font-black text-white tracking-tight mb-3 flex items-center gap-2">
                  <UserPlus className="w-5 h-5" style={{ color: '#34E3A4' }} />
                  Referral Code
                </h3>
                {currentUser?.referral_code ? (
                  <ReferralCodeReveal code={currentUser.referral_code} />
                ) : (
                  <p className="text-sm text-white/60">
                    Your unique code will appear here shortly.
                  </p>
                )}
                <p className="text-xs text-white/50 mt-3">
                  Friends who sign up with your code earn you emeralds.
                  Your code is only visible to you — public profile
                  never shows it.
                </p>
              </div>
              )}

              {/* Game Stats Card — LIVE games only (practice never counted) */}
              <div
                className="rounded-[22px] border border-white/10 backdrop-blur-[14px] p-4"
                style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
                data-testid="profile-game-stats-card"
              >
                <h3 className="text-lg font-black text-white tracking-tight mb-1 flex items-center gap-2">
                  <Star className="w-5 h-5" style={{ color: '#E6C34A' }} />
                  Game Stats
                </h3>
                <p className="text-xs text-white/50 mb-3">Live games only · practice not counted</p>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-white/85 text-sm">Win Rate</span>
                    <span className="font-black font-mono" style={{ color: '#34E3A4' }} data-testid="profile-stats-winrate">{stats.gamesWon + stats.gamesLost > 0 ? Math.round((stats.gamesWon / (stats.gamesWon + stats.gamesLost)) * 100) : 0}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">Games Won</span>
                    <span className="text-white font-black font-mono" data-testid="profile-stats-wins">{stats.gamesWon}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">Games Lost</span>
                    <span className="text-white font-black font-mono" data-testid="profile-stats-losses">{stats.gamesLost}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">Current Streak</span>
                    <span className="font-black font-mono flex items-center gap-1" style={{ color: '#E6C34A' }} data-testid="profile-stats-streak">
                      {stats.winStreak}
                      <span className="text-xs">🔥</span>
                    </span>
                  </div>
                </div>
                {/* Per-game breakdown (live only) */}
                <div className="mt-4 pt-3 border-t border-white/10 space-y-2" data-testid="profile-stats-by-game">
                  {[
                    { key: 'spades', label: 'Spades' },
                    { key: 'four_in_a_row', label: '4 In A Row' },
                    { key: 'crazy_eights', label: 'Crazy Eights' },
                    { key: 'checkers', label: 'Checkers' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex justify-between text-sm" data-testid={`profile-stats-by-game-${key}`}>
                      <span className="text-white/60">{label}</span>
                      <span className="font-bold font-mono">
                        <span style={{ color: '#34E3A4' }}>{currentUser?.by_game?.[key]?.w || 0}W</span>
                        <span className="text-white/40 mx-1">-</span>
                        <span style={{ color: '#FF3B57' }}>{currentUser?.by_game?.[key]?.l || 0}L</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-6">
              {/* Content Tabs */}
              <Tabs defaultValue="posts" className="w-full">
                <TabsList
                  className="w-full border border-white/10 backdrop-blur-[14px]"
                  style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
                >
                  <TabsTrigger value="posts" className="flex-1 data-[state=active]:bg-emerald-600 text-white" data-testid="tab-posts">
                    <Grid className="w-4 h-4 mr-2" /> Posts
                  </TabsTrigger>
                  <TabsTrigger value="videos" className="flex-1 data-[state=active]:bg-emerald-600 text-white" data-testid="tab-videos">
                    <Video className="w-4 h-4 mr-2" /> Videos
                  </TabsTrigger>
                  <TabsTrigger value="gallery" className="flex-1 data-[state=active]:bg-emerald-600 text-white" data-testid="tab-gallery">
                    <ImageIcon className="w-4 h-4 mr-2" /> Gallery
                  </TabsTrigger>
                </TabsList>
                
                {/* Posts Tab */}
                <TabsContent value="posts" className="mt-4 space-y-4">
                  {/* Create Post Button */}
                  {isOwner && (
                  <div
                    className="rounded-[22px] border border-white/10 backdrop-blur-[14px] p-4"
                    style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
                    data-testid="post-composer"
                  >
                    <button
                      onClick={() => setShowPostModal(true)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg text-white/60 hover:bg-white/5 transition-colors text-left border border-white/10"
                      style={{ backgroundColor: 'rgba(10,15,25,0.55)' }}
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(135deg,#34E3A4,#22C98C)' }}>
                        {profileData.name[0]}
                      </div>
                      <span>What&apos;s on your mind?</span>
                    </button>
                    <div className="flex justify-around mt-3 pt-3 border-t border-white/10">
                      <button
                        onClick={() => { setPostAttachmentMode('video'); setShowPostModal(true); }}
                        className="flex items-center gap-2 text-white/60 hover:text-red-400 transition-colors"
                        data-testid="composer-video"
                      >
                        <Video className="w-4 h-4" /> Video
                      </button>
                      <button
                        onClick={() => { setPostAttachmentMode('photo'); setShowPostModal(true); }}
                        className="flex items-center gap-2 text-white/60 hover:text-blue-400 transition-colors"
                        data-testid="composer-photo"
                      >
                        <ImageIcon className="w-4 h-4" /> Photo
                      </button>
                      {/*
                        "Go Live" removed Feb 2026: this composer only
                        opens a status-typing modal — real go-live flow
                        lives on the Live page. Leaving Video + Photo.
                      */}
                    </div>
                  </div>
                  )}
                  
                  {/* Posts Feed */}
                  {posts.map((post) => (
                    <div
                      key={post.id}
                      className="rounded-[22px] border border-white/10 backdrop-blur-[14px] overflow-hidden"
                      style={{ backgroundColor: 'rgba(16,22,32,0.62)' }}
                    >
                      {/* Post Header */}
                      <div className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(135deg,#34E3A4,#22C98C)' }}>
                          {profileData.name[0]}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-bold">{profileData.name}</p>
                          <p className="text-white/50 text-xs">{post.timestamp}</p>
                        </div>
                        {isOwner && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10" data-testid={`post-menu-${post.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                            <DropdownMenuItem
                              onClick={() => handleDeletePost(post.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                              data-testid={`delete-post-${post.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Post
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                      </div>

                      {/* Post Content */}
                      <p className="px-4 text-white/90">{post.content}</p>
                      {/* Round 34: what the feed shows under the caption shows here too */}
                      {post.raw && <div className="px-4 mt-2"><CurrentEventsPostBody post={post.raw} /></div>}

                      {/* Post Media */}
                      {post.thumbnail && (
                        <div
                          className="mt-3 relative cursor-pointer"
                          onClick={() => openMediaLightbox(post.thumbnail, post.type === 'video' ? 'video' : 'image', post.content)}
                        >
                          <img src={post.thumbnail} alt="" className="w-full h-64 object-cover" />
                          {post.type === 'video' && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <Play className="w-8 h-8 text-white ml-1" />
                              </div>
                              {post.views && (
                                <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-white text-xs font-mono">
                                  {post.views.toLocaleString()} views
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Post Actions */}
                      <div className="p-4 flex items-center justify-between border-t border-white/10 mt-3">
                        {(() => {
                          const alreadyLiked = (post.liked_by || []).includes(loggedInUser?.id);
                          const disabled = isOwner || alreadyLiked;
                          return (
                            <button
                              className="flex items-center gap-2 transition-colors"
                              style={{
                                color: alreadyLiked ? '#E56CA0' : 'rgba(255,255,255,0.6)',
                                cursor: disabled ? 'default' : 'pointer',
                                opacity: disabled ? 0.75 : 1,
                              }}
                              onClick={() => handleLikePost(post.id)}
                              disabled={disabled}
                              title="Like"
                              data-testid={`profile-like-post-${post.id}`}
                            >
                              <RoseQuartzIcon className="w-5 h-5" style={{ opacity: alreadyLiked ? 1 : 0.55 }} />
                              <span className="font-mono">{post.likes}</span>
                              <span className="text-[10px] uppercase tracking-wide opacity-70 inline-flex items-center gap-0.5">1 <RoseQuartzIcon className="w-3 h-3" />/like</span>
                            </button>
                          );
                        })()}
                        <button className="flex items-center gap-2 text-white/60 hover:text-blue-400 transition-colors">
                          <MessageCircle className="w-5 h-5" />
                          <span className="font-mono">{post.comments}</span>
                        </button>
                        <button className="flex items-center gap-2 text-white/60 hover:text-emerald-400 transition-colors">
                          <Share className="w-5 h-5" />
                          <span>Share</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </TabsContent>
                
                {/* Videos Tab */}
                <TabsContent value="videos" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    {posts.filter(p => p.type === 'video').map((post) => (
                      <Card
                        key={post.id}
                        className="bg-slate-800/50 border-slate-700 overflow-hidden cursor-pointer hover:border-emerald-500/50 transition-colors"
                        onClick={() => openMediaLightbox(post.thumbnail, 'video', post.content)}
                      >
                        <div className="relative">
                          <img src={post.thumbnail} alt="" className="w-full h-40 object-cover" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                              <Play className="w-6 h-6 text-white ml-1" />
                            </div>
                          </div>
                          {post.views && (
                            <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-white text-xs">
                              {post.views.toLocaleString()} views
                            </div>
                          )}
                        </div>
                        <CardContent className="p-3">
                          <p className="text-white text-sm line-clamp-2">{post.content}</p>
                          <p className="text-gray-500 text-xs mt-1">{post.timestamp}</p>
                        </CardContent>
                      </Card>
                    ))}
                    {posts.filter(p => p.type === 'video').length === 0 && (
                      <div className="col-span-2 text-center py-12">
                        <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No videos yet</p>
                        <Button onClick={() => setShowPostModal(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-500">
                          Upload Video
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* Gallery Tab */}
                <TabsContent value="gallery" className="mt-4">
                  <div className="grid grid-cols-3 gap-2">
                    {posts.filter(p => p.thumbnail).map((post) => (
                      <div
                        key={post.id}
                        className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => openMediaLightbox(post.thumbnail, post.type === 'video' ? 'video' : 'image', post.content)}
                      >
                        <img src={post.thumbnail} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    {posts.filter(p => p.thumbnail).length === 0 && (
                      <div className="col-span-3 text-center py-12">
                        <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No photos yet</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}
      </div>
      
      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share className="w-5 h-5 text-emerald-500" />
              Share Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Button onClick={shareToFriends} className="w-full justify-start bg-emerald-600 hover:bg-emerald-500">
              <Users className="w-5 h-5 mr-3" />
              Share to Friends (In-App)
            </Button>
            <Button onClick={shareToWhatsApp} variant="outline" className="w-full justify-start border-green-500 text-green-500 hover:bg-green-500/10">
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Share via WhatsApp
            </Button>
            <Button onClick={shareToSMS} variant="outline" className="w-full justify-start">
              <MessageCircle className="w-5 h-5 mr-3" />
              Share via SMS
            </Button>
            <Button onClick={copyProfileLink} variant="outline" className="w-full justify-start">
              <Copy className="w-5 h-5 mr-3" />
              Copy Profile Link
            </Button>
            <Button disabled variant="outline" className="w-full justify-start opacity-50">
              <Users className="w-5 h-5 mr-3" />
              Share to All (Coming Soon - Earn Feature)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Share to Friends Modal */}
      <Dialog open={showShareToFriendsModal} onOpenChange={setShowShareToFriendsModal}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Users className="w-5 h-5 text-emerald-500" />
              Share to Friends
            </DialogTitle>
          </DialogHeader>
          
          {/* Message to share */}
          <div className="mb-4">
            <label className="block text-gray-400 text-sm mb-2">Message</label>
            <Textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              className="bg-slate-900/50 border-slate-600 text-white placeholder-gray-400 text-white"
              rows={2}
              placeholder="Add a message..."
            />
          </div>
          
          {/* Friends list */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {loadingFriends ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-400 mt-2">Loading friends...</p>
              </div>
            ) : friendsToShare.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">No friends to share with yet</p>
              </div>
            ) : (
              friendsToShare.map((friend) => (
                <div 
                  key={friend.id}
                  onClick={() => toggleFriendSelection(friend.id)}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedFriendsToShare.includes(friend.id) 
                      ? 'bg-emerald-600/20 border border-emerald-500' 
                      : 'hover:bg-white/5'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFriendsToShare.includes(friend.id)}
                    onChange={() => toggleFriendSelection(friend.id)}
                    className="w-4 h-4 rounded border-gray-600 text-emerald-600"
                  />
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold overflow-hidden">
                    {friend.picture ? (
                      <img src={friend.picture} alt={friend.name} className="w-full h-full object-cover" />
                    ) : (
                      friend.name?.[0] || '?'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{friend.name}</p>
                    <p className="text-emerald-400 text-sm">@{friend.handle || 'user'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Send button */}
          <div className="flex gap-3 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowShareToFriendsModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={sendShareToFriends}
              disabled={selectedFriendsToShare.length === 0}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500"
            >
              Share ({selectedFriendsToShare.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Create Post Modal */}
      <Dialog
        open={showPostModal}
        onOpenChange={(open) => {
          setShowPostModal(open);
          // Reset attachment mode + preview when modal closes so the
          // next open starts fresh instead of showing stale media.
          if (!open) {
            setPostAttachmentMode('any');
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {postAttachmentMode === 'photo' ? 'Create Photo Post'
                : postAttachmentMode === 'video' ? 'Create Video Post'
                : 'Create Post'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold">
                {profileData.name[0]}
              </div>
              <div>
                <p className="text-white font-medium">{profileData.name}</p>
                <p className="text-gray-500 text-xs">@{profileData.gemsTag}</p>
              </div>
            </div>
            
            <Textarea
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="What's on your mind?"
              className="min-h-[100px] bg-slate-900/50 border-slate-600 text-white placeholder-gray-400"
              data-testid="post-modal-textarea"
            />
            
            {newPostMedia && (
              <div className="relative">
                {newPostMediaType === 'video' ? (
                  <video src={newPostMedia} className="w-full h-48 object-cover rounded-lg" controls />
                ) : (
                  <img src={newPostMedia} alt="" className="w-full h-48 object-cover rounded-lg" />
                )}
                <button 
                  onClick={() => { setNewPostMedia(null); setNewPostMediaType(null); }}
                  className="absolute top-2 right-2 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            )}
            
            {/*
              Feb 2026: only render the attachment control the user
              actually chose from the composer. If they hit the
              generic "What's on your mind?" tile, show both so
              they can still attach one — otherwise show only the
              relevant one (no duplicate Photo/Video choice).
            */}
            {!newPostMedia && (
              <div className="flex items-center gap-2 pt-2 border-t border-slate-700" data-testid="post-modal-attachment-row">
                {(postAttachmentMode === 'photo' || postAttachmentMode === 'any') && (
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-700 cursor-pointer transition-colors" data-testid="post-modal-photo">
                    <ImageIcon className="w-5 h-5 text-blue-400" />
                    <span className="text-gray-400 text-sm">Attach photo</span>
                    <input type="file" accept="image/*" onChange={handlePostMediaUpload} className="hidden" />
                  </label>
                )}
                {(postAttachmentMode === 'video' || postAttachmentMode === 'any') && (
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-700 cursor-pointer transition-colors" data-testid="post-modal-video">
                    <Video className="w-5 h-5 text-red-400" />
                    <span className="text-gray-400 text-sm">Attach video</span>
                    <input type="file" accept="video/*" onChange={handlePostMediaUpload} className="hidden" />
                  </label>
                )}
              </div>
            )}
            
            <Button onClick={handleCreatePost} className="w-full bg-emerald-600 hover:bg-emerald-500" data-testid="post-modal-submit">
              <Send className="w-4 h-4 mr-2" />
              Post
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Social List Modal (Friends/Following/Fans) */}
      <Dialog open={showSocialModal} onOpenChange={setShowSocialModal}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Users className="w-5 h-5 text-emerald-500" />
              {socialListType === 'friends' ? 'Friends' : socialListType === 'following' ? 'Following' : 'Fans'}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-1" data-testid="social-list-modal">
            {loadingSocial ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-400 mt-2">Loading...</p>
              </div>
            ) : socialListData.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">No {socialListType} yet</p>
              </div>
            ) : (
              socialListData.map((person) => (
                <div 
                  key={person.id} 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors group"
                  data-testid={`social-user-${person.id}`}
                >
                  <div 
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    onClick={() => {
                      setShowSocialModal(false);
                      navigate(`/profile/${person.handle || person.id}`);
                    }}
                  >
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0">
                      {person.picture ? (
                        <img src={person.picture} alt={person.name} className="w-full h-full object-cover" />
                      ) : (
                        person.name?.[0] || '?'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-white font-medium truncate text-sm">{person.name}</p>
                        {person.badge_type === 'sapphire' && <Star className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />}
                        {person.is_bot && <span className="text-[10px] bg-purple-500/30 text-purple-300 px-1.5 py-0.5 rounded flex-shrink-0">AI</span>}
                      </div>
                      <p className="text-emerald-400 text-xs truncate">@{person.handle || 'user'}</p>
                    </div>
                  </div>
                  {person.id !== currentUser?.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSocialAction(person.id, person.relationship); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-colors ${
                        person.relationship === 'friends' ? 'bg-emerald-600/20 text-emerald-400 hover:bg-red-600/20 hover:text-red-400' :
                        person.relationship === 'following' ? 'bg-blue-600/20 text-blue-400 hover:bg-red-600/20 hover:text-red-400' :
                        person.relationship === 'fan' ? 'bg-emerald-600 text-white hover:bg-emerald-500' :
                        'bg-emerald-600 text-white hover:bg-emerald-500'
                      }`}
                      data-testid={`social-action-${person.id}`}
                    >
                      {person.relationship === 'friends' ? 'Friends' :
                       person.relationship === 'following' ? 'Following' :
                       person.relationship === 'fan' ? 'Follow Back' :
                       'Follow'}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Photo Gallery Viewer Modal — kept as-is for legacy triggers */}
      <Dialog open={showPhotoGallery} onOpenChange={setShowPhotoGallery}>
        <DialogContent className="sm:max-w-2xl bg-slate-900 border-slate-700 p-0">
          <div className="relative">
            {profilePhotos[currentPhotoIndex] && (
              <img 
                src={profilePhotos[currentPhotoIndex]} 
                alt={`Photo ${currentPhotoIndex + 1}`}
                className="w-full max-h-[70vh] object-contain"
              />
            )}
            
            {/* Navigation arrows */}
            {profilePhotos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center"
                >
                  ←
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center"
                >
                  →
                </button>
              </>
            )}
            
            {/* Photo indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {profilePhotos.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentPhotoIndex(idx)}
                  className={`w-2 h-2 rounded-full ${idx === currentPhotoIndex ? 'bg-emerald-500' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════
          FULLSCREEN LIGHTBOX (Feb 2026) — tap slideshow photo to
          enlarge; tap anywhere to close.
          ══════════════════════════════════════════════════════════ */}
      {showLightbox && profilePhotos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md"
          style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
          onClick={() => setShowLightbox(false)}
          data-testid="lightbox-overlay"
          role="dialog"
          aria-label="Photo lightbox"
        >
          <button
            className="absolute top-4 right-4 w-11 h-11 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
            onClick={(e) => { e.stopPropagation(); setShowLightbox(false); }}
            aria-label="Close lightbox"
          >
            <X className="w-6 h-6" />
          </button>
          {profilePhotos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + profilePhotos.length) % profilePhotos.length); }}
                aria-label="Previous"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % profilePhotos.length); }}
                aria-label="Next"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}
          <img
            src={profilePhotos[lightboxIndex]}
            alt={`Photo ${lightboxIndex + 1}`}
            className="max-w-[92vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3">
            {(() => {
              const pl = photoLikes[String(lightboxIndex)] || {};
              return (
                <button
                  onClick={(e) => { e.stopPropagation(); handleLikePhoto(lightboxIndex); }}
                  disabled={isOwner || pl.liked}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full text-white"
                  style={{ backgroundColor: 'rgba(255,255,255,0.14)', cursor: (isOwner || pl.liked) ? 'default' : 'pointer' }}
                  title={isOwner ? 'Photo likes' : 'Like this photo'}
                  data-testid={`lightbox-photo-like-${lightboxIndex}`}
                >
                  <RoseQuartzIcon className="w-4 h-4" style={{ opacity: pl.liked ? 1 : 0.6 }} />
                  <span className="text-sm font-mono">{pl.likes || 0}</span>
                </button>
              );
            })()}
            <div className="px-3 py-1 rounded-full text-sm font-mono text-white" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
              {lightboxIndex + 1} / {profilePhotos.length}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          BACKGROUND PICKER MODAL — upload + Fill/Fit/Tile/Center/
          Blur style selector + overlay-darkness slider + clear.
          ══════════════════════════════════════════════════════════ */}
      <Dialog
        open={showBackgroundModal}
        onOpenChange={(open) => {
          if (!open) cancelBackground();
        }}
      >
        <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700 text-white" data-testid="background-picker-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" style={{ color: '#34E3A4' }} />
              Change page background
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Preview — always renders from DRAFT state */}
            <div className="rounded-xl overflow-hidden border border-white/10 h-32 relative bg-black/40">
              {draftBgImage ? (
                <>
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url(${draftBgImage})`,
                      backgroundSize: draftBgStyle === 'contain' ? 'contain' : draftBgStyle === 'repeat' ? '80px' : draftBgStyle === 'center' ? 'auto' : 'cover',
                      backgroundRepeat: draftBgStyle === 'repeat' ? 'repeat' : 'no-repeat',
                      backgroundPosition: 'center',
                      filter: draftBgStyle === 'blur' ? 'blur(12px) saturate(120%)' : 'none',
                      transform: draftBgStyle === 'blur' ? 'scale(1.05)' : 'none',
                    }}
                  />
                  <div className="absolute inset-0" style={{ backgroundColor: `rgba(6,10,18,${draftBgOverlay})` }} />
                  <div className="absolute bottom-2 left-3 right-3 text-xs text-white/85">Preview — hit Save to apply</div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">No background — upload to preview</div>
              )}
            </div>

            {/* Upload button */}
            <label
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg cursor-pointer font-bold"
              style={{ backgroundColor: '#22C98C', color: '#062015' }}
              data-testid="background-upload-btn"
            >
              {uploadingBackground ? (
                <span>Uploading…</span>
              ) : (
                <>
                  <Camera className="w-4 h-4" />
                  Upload image
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUploadBackground(f);
                  e.target.value = '';
                }}
                className="hidden"
              />
            </label>

            {/* Style picker — updates DRAFT only */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Style</label>
              <div className="grid grid-cols-5 gap-2">
                {BACKGROUND_STYLE_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setDraftBgStyle(opt.key)}
                    className="px-2 py-2 rounded-lg text-xs font-bold border transition-all"
                    style={
                      draftBgStyle === opt.key
                        ? { backgroundColor: 'rgba(52,227,164,0.18)', borderColor: '#34E3A4', color: '#34E3A4' }
                        : { backgroundColor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }
                    }
                    data-testid={`bg-style-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Overlay slider — draft only */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-widest text-white/60">Overlay darkness</label>
                <span className="text-xs font-mono text-white/85">{Math.round(draftBgOverlay * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={draftBgOverlay}
                onChange={(e) => setDraftBgOverlay(parseFloat(e.target.value))}
                className="w-full accent-emerald-400"
                data-testid="background-overlay-slider"
              />
            </div>

            {draftBgImage && (
              <button
                onClick={handleClearBackground}
                className="text-sm text-white/60 hover:text-red-400 underline"
                data-testid="background-clear-btn"
              >
                Clear background
              </button>
            )}

            {/* Save / Cancel footer (Feb 2026 — explicit apply) */}
            <div className="flex gap-2 pt-2 border-t border-white/10">
              <Button
                variant="outline"
                onClick={cancelBackground}
                className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5"
                disabled={savingBackground}
                data-testid="background-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={saveBackground}
                disabled={savingBackground || uploadingBackground}
                className="flex-1 font-bold"
                style={{ backgroundColor: '#22C98C', color: '#062015' }}
                data-testid="background-save-btn"
              >
                {savingBackground ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden <audio> elements (Feb 2026 anthem player).
          `audioRef` is the main pill player — loops, muted-autoplays
          on visitor open, unmutes on first user tap. `previewAudioRef`
          is a separate element used ONLY inside the picker so
          previewing a track doesn't disturb the main player. */}
      <audio ref={audioRef} preload="none" loop playsInline data-testid="anthem-audio" />
      <audio
        ref={previewAudioRef}
        preload="none"
        playsInline
        onEnded={() => setPreviewAnthemId(null)}
        onPause={() => {
          // If the ended callback fires or user pauses, drop the flag
          // so the picker's play/pause icon reflects reality.
          if (previewAudioRef.current?.paused) setPreviewAnthemId(null);
        }}
      />

      {/* ══════════════════════════════════════════════════════════
          ANTHEM PICKER MODAL — royalty-free catalog (Feb 2026).
          One preview player per row; tapping "Choose" saves.
          ══════════════════════════════════════════════════════════ */}
      <Dialog
        open={showAnthemPicker}
        onOpenChange={(open) => {
          setShowAnthemPicker(open);
          if (!open && previewAudioRef.current) {
            try { previewAudioRef.current.pause(); } catch (_) {}
            setPreviewAnthemId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700 text-white" data-testid="anthem-picker-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Music className="w-5 h-5" style={{ color: '#34E3A4' }} />
              Pick your anthem
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60 -mt-2 mb-2">
            Every track is royalty-free. No uploads — pick from the
            curated list. Your anthem loops on your profile page.
          </p>
          {/* Genre filter row (Feb 2026 v2) */}
          <div className="flex gap-2 flex-wrap pb-1" data-testid="anthem-genre-filter">
            {['All', ...anthemGenres].map(g => (
              <button
                key={g}
                onClick={() => setAnthemGenreFilter(g)}
                className="px-3 py-1 rounded-full text-xs font-bold border transition-colors"
                style={
                  anthemGenreFilter === g
                    ? { backgroundColor: 'rgba(52,227,164,0.18)', borderColor: '#34E3A4', color: '#34E3A4' }
                    : { backgroundColor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }
                }
                data-testid={`anthem-genre-${g.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="max-h-96 overflow-y-auto space-y-1.5" data-testid="anthem-picker-list">
            {anthemCatalog.length === 0 ? (
              <div className="text-center py-6">
                <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-white/60 mt-2 text-sm">Loading library…</p>
              </div>
            ) : (
              anthemCatalog
                .filter(t => anthemGenreFilter === 'All' || t.genre === anthemGenreFilter)
                .map(track => {
                const isSelected = anthem?.id === track.id;
                const isPreviewing = previewAnthemId === track.id;
                return (
                  <div
                    key={track.id}
                    className="flex items-center gap-3 p-2.5 rounded-lg border transition-colors"
                    style={{
                      backgroundColor: isSelected ? 'rgba(52,227,164,0.10)' : 'rgba(10,15,25,0.55)',
                      borderColor: isSelected ? 'rgba(52,227,164,0.35)' : 'rgba(255,255,255,0.10)',
                    }}
                    data-testid={`anthem-row-${track.id}`}
                  >
                    <button
                      onClick={() => togglePreview(track)}
                      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: isPreviewing ? '#34E3A4' : 'rgba(255,255,255,0.08)', color: isPreviewing ? '#062015' : '#34E3A4' }}
                      aria-label={isPreviewing ? 'Stop preview' : 'Preview track'}
                      data-testid={`anthem-preview-${track.id}`}
                    >
                      {isPreviewing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{track.title}</p>
                      <p className="text-xs text-white/60 truncate">
                        {track.artist}
                        {track.genre && <span className="ml-2 text-white/40">· {track.genre}</span>}
                      </p>
                    </div>
                    <button
                      onClick={() => saveAnthemChoice(track)}
                      disabled={savingAnthem}
                      className="text-xs font-bold px-3 py-1.5 rounded-full shrink-0"
                      style={
                        isSelected
                          ? { backgroundColor: 'rgba(52,227,164,0.18)', color: '#34E3A4', border: '1px solid rgba(52,227,164,0.35)' }
                          : { backgroundColor: '#22C98C', color: '#062015' }
                      }
                      data-testid={`anthem-choose-${track.id}`}
                    >
                      {isSelected ? 'Current' : 'Choose'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <button
              onClick={() => saveAnthemChoice(null)}
              disabled={!anthem || savingAnthem}
              className="text-sm text-white/60 hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed underline"
              data-testid="anthem-clear-btn"
            >
              Clear anthem
            </button>
            <p className="text-[11px] text-white/40 text-right">Royalty-free library · attribution shown per track</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════
          TOP-8 VIEW-ALL MODAL — search all friends, tap to pin, if
          all 8 slots full prompt which slot to replace.
          ══════════════════════════════════════════════════════════ */}
      <Dialog open={showTop8Modal} onOpenChange={setShowTop8Modal}>
        <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700 text-white" data-testid="top8-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5" style={{ color: '#34E3A4' }} />
              Your Top 8 · {top8Ids.length}/8 filled
            </DialogTitle>
          </DialogHeader>

          {/* Replace-slot prompt */}
          {pendingPickFriend && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm mb-2">
              <div className="font-bold mb-2 text-amber-200">Your Top 8 is full. Which slot should <span className="font-black">{pendingPickFriend.name}</span> replace?</div>
              <div className="grid grid-cols-4 gap-2">
                {top8Cards.map((c, i) => (
                  <button
                    key={c.id}
                    onClick={() => confirmReplaceTop8Slot(i)}
                    className="rounded-lg overflow-hidden aspect-square border border-white/10 hover:border-red-400/60 relative"
                    data-testid={`replace-slot-${i}`}
                  >
                    {c.picture ? (
                      <img src={c.picture} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-emerald-600 flex items-center justify-center text-white font-black">{(c.name || '?')[0]}</div>
                    )}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-xs font-bold opacity-0 hover:opacity-100 transition-opacity">Replace</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setPendingPickFriend(null); setPickingReplaceSlot(null); }}
                className="mt-2 text-xs text-white/60 hover:text-white underline"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
            <Input
              value={top8Search}
              onChange={(e) => setTop8Search(e.target.value)}
              placeholder="Search your friends…"
              className="pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-white/40"
              data-testid="top8-search-input"
            />
          </div>

          {/* Friends list */}
          <div className="max-h-96 overflow-y-auto mt-3 space-y-1" data-testid="top8-friends-list">
            {loadingAllFriends ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-white/60 mt-2 text-sm">Loading friends…</p>
              </div>
            ) : filteredFriendsForTop8.length === 0 ? (
              <p className="text-white/60 text-sm text-center py-6">
                {allFriendsForTop8.length === 0 ? 'No friends yet.' : 'No matches.'}
              </p>
            ) : (
              filteredFriendsForTop8.map(f => {
                const isPinned = top8Ids.includes(f.id);
                return (
                  <button
                    key={f.id}
                    onClick={() => handlePickTop8(f)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                    data-testid={`top8-friend-row-${f.id}`}
                  >
                    <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0">
                      {f.picture ? (
                        <img src={f.picture} alt={f.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-black" style={{ background: 'linear-gradient(135deg,#34E3A4,#22C98C)' }}>{(f.name || '?')[0]}</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{f.name}</p>
                      <p className="text-xs" style={{ color: '#34E3A4' }}>@{f.handle || 'user'}</p>
                    </div>
                    {isPinned ? (
                      <span className="text-[11px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(52,227,164,0.18)', color: '#34E3A4', border: '1px solid rgba(52,227,164,0.35)' }}>
                        Pinned
                      </span>
                    ) : (
                      <span className="text-[11px] font-bold px-2 py-1 rounded-full text-white/70 border border-white/15">
                        Pin
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Help & Support Modal */}
      <Dialog open={showHelpModal} onOpenChange={setShowHelpModal}>
        <DialogContent className="sm:max-w-xl bg-slate-900 border-slate-700 max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-blue-400" />
              Help & Support
            </DialogTitle>
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b border-slate-700 pb-2">
            <button
              onClick={() => setHelpTab('create')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                helpTab === 'create' 
                  ? 'bg-blue-500/20 text-blue-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="create-ticket-tab"
            >
              New Ticket
            </button>
            <button
              onClick={() => { setHelpTab('tickets'); fetchMyTickets(); }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                helpTab === 'tickets' 
                  ? 'bg-blue-500/20 text-blue-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="my-tickets-tab"
            >
              My Tickets ({myTickets.length})
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {helpTab === 'create' ? (
              <div className="space-y-4 py-4">
                {/* Subject */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Subject</label>
                  <Input
                    value={helpSubject}
                    onChange={(e) => setHelpSubject(e.target.value)}
                    placeholder="Brief description of your issue"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="subject-input"
                  />
                </div>
                
                {/* Category */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Category</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {TICKET_CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setHelpCategory(cat.id)}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors border ${
                          helpCategory === cat.id
                            ? 'bg-blue-500/20 text-blue-400 border-blue-500'
                            : 'bg-slate-800 text-gray-400 border-slate-700 hover:border-blue-500/50'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Message */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Message</label>
                  <Textarea
                    value={helpMessage}
                    onChange={(e) => setHelpMessage(e.target.value)}
                    placeholder="Please describe your issue in detail..."
                    rows={5}
                    className="bg-slate-800 border-slate-700 text-white resize-none"
                    data-testid="message-input"
                  />
                </div>
                
                {/* Screenshot Upload (Optional) */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Screenshot (Optional)</label>
                  {helpAttachment ? (
                    <div className="relative rounded-lg overflow-hidden">
                      <img src={helpAttachment} alt="Attachment" className="w-full h-32 object-cover" />
                      <button
                        onClick={() => setHelpAttachment(null)}
                        className="absolute top-2 right-2 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-700 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors">
                      <Camera className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-400">Add Screenshot</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = () => setHelpAttachment(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                        data-testid="screenshot-input"
                      />
                    </label>
                  )}
                </div>
                
                {/* Submit Button */}
                <Button
                  onClick={submitSupportTicket}
                  disabled={submittingTicket}
                  className="w-full bg-blue-600 hover:bg-blue-500"
                  data-testid="submit-ticket-button"
                >
                  {submittingTicket ? 'Submitting...' : 'Submit Ticket'}
                </Button>
              </div>
            ) : (
              <div className="py-4">
                {loadingTickets ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : myTickets.length === 0 ? (
                  <div className="text-center py-8">
                    <HelpCircle className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400">No support tickets yet</p>
                    <Button
                      onClick={() => setHelpTab('create')}
                      variant="outline"
                      className="mt-4 border-blue-500 text-blue-400"
                    >
                      Create Ticket
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                        data-testid={`ticket-${ticket.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-medium text-white">{ticket.subject}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full border ${getTicketStatusColor(ticket.status)}`}>
                            {ticket.status?.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-blue-400 mb-1">
                          {TICKET_CATEGORIES.find(c => c.id === ticket.category)?.label || ticket.category}
                        </p>
                        <p className="text-sm text-gray-400 line-clamp-2">{ticket.message}</p>
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-700">
                          <span className="text-xs text-gray-500">{formatTicketDate(ticket.created_at)}</span>
                          {ticket.responses?.length > 0 && (
                            <span className="text-xs text-emerald-400 flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" />
                              {ticket.responses.length} response(s)
                            </span>
                          )}
                        </div>
                        
                        {/* Show responses if any */}
                        {ticket.responses?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {ticket.responses.map((response, idx) => (
                              <div key={idx} className="pl-3 border-l-2 border-emerald-500/50 bg-emerald-500/5 p-2 rounded-r-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-emerald-400">{response.admin_name}</span>
                                  <span className="text-xs text-gray-500">{formatTicketDate(response.timestamp)}</span>
                                </div>
                                <p className="text-sm text-gray-300">{response.response}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
